import axios, { AxiosInstance, AxiosError } from 'axios';
import i18next from 'i18next';
import { API_BASE_URL } from '@/lib/constants';
import { decodeBlobErrorBody } from '@/lib/utils';
import { ACTIVE_ROLE_MISMATCH_EVENT } from '@/lib/auth-events';
import type { CopyPublishedResponse } from '@/lib/copyResolve';
import type {
  CopyEditorDetail,
  CopyMinVersionResult,
  CopySlotHistoryRow,
  CopyVersionBody,
} from '@/types/copyStore';
import type {
  ApiResponse,
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  Notification,
  ChatMessage,
  Resource,
  ResourceCategory,
  ResourceCountry,
  ResourceState,
  ResourceTreatmentPhase,
  ResourceWriteRequest,
  MfaVerifyRequest,
  MfaStatusResponse,
  MfaEnrollStartResponse,
  ConsentSettings,
  ImplicitHandoffConsent,
  NotificationSettings,
  PushCategorySettings,
  FamilyMine,
  UpdateOnboardingRequest,
  UpdatePatientRequest,
  DashboardPreferences,
  MedicalAppointment,
  CreateAppointmentRequest,
  Medication,
  MedicationDose,
  LogDoseRequest,
  DoctorQuestion,
  SymptomEntry,
  CommunityPost,
  CommunityFeedParams,
  FeedbackItem,
  FeedbackCounts,
  FeedbackType,
  FeedbackStatus,
  HandoffRequest,
  FamilyPendingResponse,
  TranslationReport,
  HandoffOrigin,
  AvailableTRFer,
  TrferCalendarEvent,
  TrferUnreadCounts,
  TrferUnreadKind,
  LegalDocMeta,
  LegalDocumentFull,
  UserLegalStatus,
  ConsentEventRow,
  GrantRequest,
  GrantTransitionInput,
  GrantDecisionContext,
  GrantCreateInput,
  ReimbursementClaimLite,
  AdminSearchEvent,
  ClientErrorSummaryRow,
  GrantAmounts,
  GrantAmountsResponse,
  AwardsQuery,
  AwardsResponse,
  AwardPaymentInput,
  AwardPaymentResult,
  ApplicationOriginReport,
  ReimbursementClaim,
  QueueClaim,
  ClaimFamilyEdits,
} from '@/types/api';
import type { DocumentUploadResult } from '@/types/api';

// Re-export legal types so components can import them from '@/services/api'.
export type { LegalDocMeta, LegalDocumentFull, UserLegalStatus, ConsentEventRow } from '@/types/api';
// BILL Spend & Expense types (F-011 slice A) — kept on their own import line
// so the shared block above stays untouched for concurrent work.
import type { SpendQueueData, SpendSyncResult, SpendTransaction, SpendGrantUsage, DocusignDisclosure, SystemHealth } from '@/types/api';
import type { LanguageCode } from '@/config/languages';
import type { PlatformConfig } from '@/pages/admin/types';
import type { FamilyApplication, ApplicationDecisionPayload, ApplicationDocumentMeta, AddressValidationData, ReferralMethod, FieldEditRequest, FieldEditResult, ChangeHistoryRow, ChaseBucket, ApplicationDocumentChase } from '@/types/applications';
// #2238: the committee prose client's shapes, on their own line so the line above stays untouched.
import type { CommitteeHistory, CommitteeProseSlot, CommitteeProseWriteMeta } from '@/types/applications';
import type { ApplicationDeletePreview, ApplicationDeleteResult } from '@/types/applications';
// #2239: the committee suggestions-and-comments client's shapes, on their own line.
import type {
  CommitteeAnchor, CommitteeAnnotations, CommitteeCommentRecord, CommitteeSuggestionCategory,
  CommitteeSuggestionDecisionMeta, CommitteeSuggestionRecord,
} from '@/types/applications';
import type { CommitteeRecommendedOutcome } from '@/types/applications';
import type {
  CommitteePresentationFamilies, CommitteePresentationFile, CommitteePresentationSummary, CommitteeRegionStatsRegion,
  CommitteeRegionStatsValues, PresentationTag,
} from '@/types/applications';
// #2229: the "Draft with assistant" shapes, on their own line.
import type { CommitteeBioDraftStart, CommitteeBioDraftState } from '@/types/applications';
// #2243: the assistant's suggested-edits and fact-guard shapes, on their own line.
import type { CommitteeAssistStart, CommitteeAssistState } from '@/types/applications';
// #2244: the assistant's acceptance-rate report, on its own line.
import type { CommitteeLlmSuggestionStats } from '@/types/applications';
import type { DecisionNoticeCheck } from '@/types/applications';
import type { DecisionNoticeSend } from '@/types/applications';
import type { MoreInfoPreview } from '@/types/applications';
// 2026-10-10: the intake review (application review redesign).
import type { IntakeEvidence, IntakeReview, IntakeReviewJob } from '@/types/applications';
import type { AnswerTranslations } from '@/types/applications';
import type {
  FormDefinitionSummary, FormDefinitionVersion,
  FormDefinitionDoc, FormDetail, FormVersionDoc, SaveDraftPayload,
  FieldRetireImpact,
} from '@/lib/formDefinitions';
// PR-6 send pipeline — its own import line and its own types module, so the
// concurrent PR-5 edits to this file and to lib/formDefinitions.ts merge clean.
import type {
  SendInstance, SendRequest, SendCandidateFamily, SendCandidateFamiliesResult, SendResultsPayload,
  SendScope, SendTrferOption, SendCandidateTrfersResult,
} from '@/types/formSends';
import { getFamilyDisplayName } from '@/lib/family-display';
import type {
  Poll, PollFeedItem, PollResultsResponse, VoteAnswer, PollAudience,
  QuestionType, ResultsVisibility, PollStatus, AttributedResponsesPayload, ListMeta,
  NonRespondersPayload, RemindResult,
} from '@/types/polls';

/**
 * #1108. `ApiResponse` is a discriminated union whose success arm types `meta`
 * as `Record<string, unknown>`; intersecting that with a concrete `ListMeta`
 * produces a type `ListMeta` cannot satisfy. This is the shape sendInstances
 * actually returns — it always constructs the success arm — so naming it
 * directly is both simpler and more truthful than widening ListMeta.
 */
type SendInstancesResult = {
  status: 'success';
  success: true;
  data: SendInstance[];
  meta?: ListMeta;
};

/**
 * A stored dose assertion (#846 D-A). W2-B landed the canonical
 * `MedicationDose` in types/api.ts (mirroring the backend writer); this
 * alias keeps the #847 detail-view consumers on the single shape.
 */
export type MedicationDoseEvent = MedicationDose;

/**
 * API Client Configuration
 */
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');

/**
 * Create axios instance with default config
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: API_TIMEOUT,
  // withCredentials sends the httpOnly refresh cookie on web (the cookie is how
  // /auth/refresh authenticates when the in-memory access token is gone). On
  // native there is no cookie, so this is a harmless no-op.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Auth token storage — goes through the storage.ts abstraction. On web, that
 * preserves the existing localStorage (remember-me) / sessionStorage split.
 * On native (Capacitor), writes mirror to @capacitor/preferences so tokens
 * survive WKWebView eviction under memory pressure. See storage.ts for
 * details. Callers should prefer `authStorage.*` directly; `clearAuthTokens`
 * is kept as a back-compat alias for existing logout paths.
 */
import { authStorage } from './storage';
import { isNative } from '@/lib/platform';
import {
  IMPERSONATION_ENDED_EVENT, isImpersonationToken, ownerOfToken, reloadForIdentity, tabOwner, userIdOfToken,
} from '@/lib/auth-sync';

export const clearAuthTokens = () => authStorage.clearAll();

/**
 * #2109 R2-B6: every request carries the reader's UI language as
 * `X-TRF-Locale`. `LocaleProvider` is the one writer of the i18next language
 * (it negotiates, then `changeLanguage`), so reading it here follows the
 * provider without a second piece of state. The server intersects it with a
 * form's `offered_languages` and the five UI codes before it stamps anything,
 * so an unexpected value is harmless; before negotiation settles there is no
 * language and no header, and the server falls back to the family's saved
 * preference, then English.
 */
export const REQUEST_LOCALE_HEADER = 'X-TRF-Locale';
export function requestLocale(): string | undefined {
  const lng = i18next.language;
  return typeof lng === 'string' && /^[a-z]{2,3}$/.test(lng) ? lng : undefined;
}

/**
 * Request interceptor - Add auth token and the reader's language. Async so it
 * can await the native Preferences read; axios supports async interceptors.
 */
apiClient.interceptors.request.use(
  async (config) => {
    const token = await authStorage.get('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const locale = requestLocale();
    if (locale) config.headers[REQUEST_LOCALE_HEADER] = locale;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Token refresh state.
 *
 * INVARIANT: isRefreshing must be set to false and refreshSubscribers must be
 * replaced with a new empty array BEFORE subscriber callbacks are invoked.
 * Callbacks may trigger reentrant 401s; resetting state first ensures those
 * reentrant requests start a new refresh cycle rather than queuing to a
 * drained (dead) subscriber list.
 */
let isRefreshing = false;
type RefreshSubscriber = { onToken: (token: string) => void; onFail: () => void };
let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Notify the app that the interceptor has cleared stored credentials so
 * AuthContext (or any other listener) can drop its user state. We do NOT
 * navigate from here — auth-gated routes are protected by RoleGatedRoute,
 * which redirects to the appropriate home page once the user state goes
 * null. Public surfaces like /login don't need to redirect and shouldn't
 * be forced to (an interceptor-driven redirect would race with the
 * AuthContext mount effect and produce /login → /login refresh loops on
 * any page that happens to call an authenticated endpoint).
 */
/**
 * B-596: the account's active role is ONE server-side field shared by every
 * browser it is signed into. A shell left open after the account switched
 * roles elsewhere gets `403 { code: 'WRONG_ACTIVE_ROLE', details.active_role }`
 * on every route the old hat served. Raise it ONCE per observed role so
 * AuthContext can re-hydrate `/me` and move the shell — a burst of 403s from
 * one page load must not become a burst of refreshes and toasts.
 */
let lastMismatchAnnounced: { role: string; at: number } | null = null;
const MISMATCH_DEDUPE_MS = 5_000;

function notifyActiveRoleMismatch(activeRole: string | null | undefined) {
  if (typeof window === 'undefined') return;
  const role = activeRole ?? '';
  const now = Date.now();
  if (lastMismatchAnnounced && lastMismatchAnnounced.role === role && now - lastMismatchAnnounced.at < MISMATCH_DEDUPE_MS) return;
  lastMismatchAnnounced = { role, at: now };
  window.dispatchEvent(new CustomEvent(ACTIVE_ROLE_MISMATCH_EVENT, { detail: { activeRole: role } }));
}

function notifyAuthCleared() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('auth:cleared'));
}

/** #2478: see IMPERSONATION_ENDED_EVENT in lib/auth-sync.ts. */
function notifyImpersonationEnded() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(IMPERSONATION_ENDED_EVENT));
}

/**
 * #2439: a failed refresh ends this tab's session, unless the tab adopted
 * someone else while the refresh was out (a sign-in that landed in between).
 * Then the failure belonged to the previous session, and clearing would erase
 * the new person's tokens and sign them out.
 */
async function clearUnlessSignedInSince(holderWhenSent: string | null) {
  if (tabOwner() !== holderWhenSent) return;
  await authStorage.clearAll();
  if (tabOwner() !== holderWhenSent) return;
  notifyAuthCleared();
}

/**
 * Resolves all queued requests after a successful token refresh.
 * State is reset atomically before callbacks fire to prevent reentrant
 * 401 responses from being stranded in a dead queue.
 */
const onRefreshed = (token: string) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  isRefreshing = false;
  subscribers.forEach((s) => s.onToken(token));
};

/**
 * #2439: a refresh that failed or was refused settles every queued request
 * with its own 401. Dropping the queue left those promises pending forever.
 */
const onRefreshFailed = () => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  isRefreshing = false;
  subscribers.forEach((s) => s.onFail());
};

const addRefreshSubscriber = (subscriber: RefreshSubscriber) => {
  refreshSubscribers.push(subscriber);
};

// Endpoints under /auth/* that must NOT trigger the refresh-and-retry path.
// Two categories:
//   1. Pre-auth endpoints whose 401 means "wrong credentials" — refreshing
//      and retrying would be nonsense (login/register/password-reset/etc).
//   2. /auth/refresh itself — recursively refreshing on a 401 from the
//      refresh endpoint would infinite-loop.
//
// Everything else under /auth/* (sse-token, me, profile, change-password,
// switch-role, ...) is a normal authenticated call and SHOULD use the
// refresh flow. B-062: the previous `url.includes('/auth/')` exemption was
// too broad and caused every parent to be bounced to "session expired"
// after the 1h access-token TTL, even though their 7d refresh token sat
// unused in storage.
const REFRESH_SKIP_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/magic-link',     // magic-link request + magic-link verify both start with this
  '/auth/sms-code',       // sms-code request + verify
  '/auth/mfa-verify',
  '/auth/apply-signup',   // EC-2 (B-095): pre-auth signup; refresh interceptor must not mangle 409/4xx errors
] as const;

function isPreAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return REFRESH_SKIP_AUTH_ENDPOINTS.some((path) => url.includes(path));
}

// Exported for unit tests (B-062). Production callers should not import this.
export const _isPreAuthEndpoint = isPreAuthEndpoint;

/**
 * Response interceptor - Handle errors and token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest.headers['X-Retry']) {
      // Pre-auth + refresh endpoints handle their own 401s. Everything else
      // under /auth/* (sse-token, me, profile, switch-role, ...) falls
      // through to the refresh flow below — see B-062.
      if (isPreAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      // #2478: an impersonation token has no refresh token of its own. A 401
      // on one means it expired or the staff session it is bound to ended.
      // Refreshing would renew from the operator's cookie and replay this
      // request as the operator under the family's banner, so it is never
      // refreshed or queued behind a refresh: AuthContext ends the
      // impersonation and the request fails.
      const sentWith = String(originalRequest.headers.Authorization ?? '').replace(/^Bearer\s+/i, '');
      if (sentWith && isImpersonationToken(sentWith)) {
        notifyImpersonationEnded();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber({
            onToken: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            onFail: () => reject(error),
          });
        });
      }

      const native = isNative();
      const remember = authStorage.isRemembered();

      // Native: the refresh token lives in Preferences and rides in the request
      // body. No stored token means there's nothing to refresh with — clear and
      // bounce. Web: the refresh token is the httpOnly cookie (invisible to JS
      // and auto-sent via withCredentials), so there's nothing to read here and
      // we POST an empty body; the cookie does the work.
      const refreshToken = native ? await authStorage.get('refresh_token') : null;
      if (native && !refreshToken) {
        await authStorage.clearAll();
        notifyAuthCleared();
        return Promise.reject(error);
      }

      isRefreshing = true;
      originalRequest.headers['X-Retry'] = 'true';
      // #2439: who this request was for — the session owner of the bearer it
      // carried (the operator for an impersonation token), else the tab's
      // owner. A request with neither (a web cold boot) may adopt whoever the
      // cookie names.
      const holder = tabOwner();
      const sentBearer = String(originalRequest.headers.Authorization ?? '').replace(/^Bearer\s+/i, '');
      const expectedFor = (sentBearer ? ownerOfToken(sentBearer) : null) ?? holder;

      try {
        const response = await apiClient.post<ApiResponse<{ tokens: { accessToken: string; refreshToken?: string } }>>(
          '/auth/refresh',
          native ? { refreshToken } : {}
        );
        const newAccessToken = response.data.data?.tokens?.accessToken;
        const newRefreshToken = response.data.data?.tokens?.refreshToken;

        // #2439: on web the cookie is shared by every tab, so another tab's
        // sign-in may have made it someone else's. A request is never
        // replayed as a different person: when the renewed token names
        // another user than the one this tab holds, it is neither stored nor
        // used, and the tab reloads as a cross-tab message would make it.
        // A request sent as someone this tab no longer holds is simply
        // refused: the tab is already becoming whoever signs in next. The
        // check fails closed: an undecodable token, or a tab that adopted
        // someone else while the refresh was out, also refuses.
        const renewedFor = newAccessToken ? userIdOfToken(newAccessToken) : null;
        const ownerNow = tabOwner();
        const refused = newAccessToken && expectedFor !== null && (
          renewedFor !== expectedFor
          || ownerNow !== holder
          || (ownerNow !== null && ownerNow !== expectedFor)
        );
        if (refused) {
          onRefreshFailed();
          // Reload only a tab whose own identity the cookie contradicts. A tab
          // that adopted someone during the refresh is already right; the
          // stale request just fails.
          if (ownerNow !== null && ownerNow === holder && renewedFor !== ownerNow) reloadForIdentity(renewedFor);
          return Promise.reject(error);
        }

        if (newAccessToken) {
          await authStorage.set('auth_token', newAccessToken, { remember });
          // Only native returns + stores a rotated refresh token; on web the
          // cookie is rotated server-side and never appears in the body.
          if (native && newRefreshToken) await authStorage.set('refresh_token', newRefreshToken, { remember });
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          onRefreshed(newAccessToken);
          return apiClient(originalRequest);
        }

        // Refresh succeeded but returned no token — treat as failed
        onRefreshFailed();
        await clearUnlessSignedInSince(holder);
        return Promise.reject(error);
      } catch (refreshError) {
        onRefreshFailed();
        await clearUnlessSignedInSince(holder);
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401) {
      await authStorage.clearAll();
      notifyAuthCleared();
    }

    if (error.response?.status === 403) {
      const body = error.response.data as { code?: string; details?: { active_role?: string | null } } | undefined;
      if (body?.code === 'WRONG_ACTIVE_ROLE') notifyActiveRoleMismatch(body.details?.active_role);
    }

    return Promise.reject(error);
  }
);

/**
 * B6 (F-013) — social-worker invite links for /apply.
 * ApplyLandingPage stashes the ?invite= token here (sessionStorage: tab-scoped,
 * gone on close) so it survives the signup → wizard hop; applications.createDraft
 * sends it to the backend for redemption and clears it. try/catch guards
 * environments without sessionStorage access.
 */
export const APPLY_INVITE_STORAGE_KEY = 'trf-apply-invite-token';

export function stashApplyInviteToken(token: string): void {
  try { sessionStorage.setItem(APPLY_INVITE_STORAGE_KEY, token); } catch { /* storage unavailable — backend gate still enforces */ }
}

function readApplyInviteToken(): string | null {
  try { return sessionStorage.getItem(APPLY_INVITE_STORAGE_KEY); } catch { return null; }
}

function clearApplyInviteToken(): void {
  try { sessionStorage.removeItem(APPLY_INVITE_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/**
 * GH-415 — reusable application codes for /apply.
 * Same sessionStorage contract as the invite token above: ApplyLandingPage
 * (?code=) or the InviteRequiredStop code-entry form stashes the raw code here
 * so it survives the signup → wizard hop; applications.createDraft sends it for
 * redemption and clears it. Stored raw (as typed/pasted) — the backend
 * normalizes (uppercase, strip whitespace/hyphens/`TRF` prefix, O→0, I/L→1).
 */
export const APPLY_CODE_STORAGE_KEY = 'trf-apply-application-code';

export function stashApplyCode(code: string): void {
  try { sessionStorage.setItem(APPLY_CODE_STORAGE_KEY, code); } catch { /* storage unavailable — backend gate still enforces */ }
}

function readApplyCode(): string | null {
  try { return sessionStorage.getItem(APPLY_CODE_STORAGE_KEY); } catch { return null; }
}

function clearApplyCode(): void {
  try { sessionStorage.removeItem(APPLY_CODE_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/**
 * #1018 Task 14 (Part 1) — the referral "slow path" (`ReferralBlock`) value,
 * stashed as it survives the signup round-trip. Same sessionStorage contract
 * as `stashApplyCode` above — one key, not three, so the three wire fields
 * always travel and clear together. `InviteRequiredStop`'s `onReferralChange`
 * feeds this directly (the shape is structurally identical to
 * `ReferralValue` in `components/apply/ReferralBlock.tsx`); `createDraft`
 * reads it, maps it onto the three `referral_*` wire names the backend pins,
 * and clears it after a successful draft creation.
 */
export interface ApplyReferralStash {
  hospital_id: string | null;
  hospital_other: string;
  contact_name: string;
}

export const APPLY_REFERRAL_STORAGE_KEY = 'trf-apply-referral';

export function stashApplyReferral(value: ApplyReferralStash): void {
  try { sessionStorage.setItem(APPLY_REFERRAL_STORAGE_KEY, JSON.stringify(value)); } catch { /* storage unavailable — the family simply isn't attributed */ }
}

function readApplyReferral(): ApplyReferralStash | null {
  try {
    const raw = sessionStorage.getItem(APPLY_REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ApplyReferralStash;
  } catch { return null; }
}

function clearApplyReferral(): void {
  try { sessionStorage.removeItem(APPLY_REFERRAL_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/**
 * #1014 (AP-32) — the pre-screen answers, carried to application creation.
 *
 * Same sessionStorage contract as the two above, and for the same reason: the
 * family answers the pre-screen BEFORE an account exists, and the answers have
 * to survive the signup hop to reach `POST /applications`, where the
 * server-side gate (#1012 / AP-30) re-evaluates them against the published
 * definition. The client-side stop screen is a courtesy; this is what the
 * refusal is actually made of.
 *
 * Two things follow from the gate being fail-closed, and both are load-bearing:
 *
 *  - Every live required pre-screen question must be answered here, or the
 *    server refuses with 422. Sending a subset is not a partial pass, it is a
 *    lockout. All seven seeded questions are `required: true`.
 *  - Sending nothing at all is NOT a way through. A blank answer is correctly
 *    "not a miss", so an empty payload scores zero misses — the gate demands
 *    the answers exist rather than inferring consent from silence.
 *
 * Cleared alongside the invite/code stash once the draft exists. If storage is
 * unavailable the family is refused rather than admitted: the backend is the
 * gate, and it cannot read what the browser could not keep.
 */
export const APPLY_SCREENING_STORAGE_KEY = 'trf-apply-screening-answers';

/** The flat `field.id -> answer` shape the pre-screen and the gate both speak. */
export type ScreeningAnswers = Record<string, string | string[]>;

export function stashApplyScreening(answers: ScreeningAnswers): void {
  try {
    sessionStorage.setItem(APPLY_SCREENING_STORAGE_KEY, JSON.stringify(answers));
  } catch { /* storage unavailable — the server gate refuses rather than admits */ }
}

export function readApplyScreening(): ScreeningAnswers | null {
  try {
    const raw = sessionStorage.getItem(APPLY_SCREENING_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // A non-object (or an array) is not an answer set. Treat it as absent and
    // let the gate refuse, rather than posting a shape the server must reject.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as ScreeningAnswers;
  } catch { return null; }
}

export function clearApplyScreening(): void {
  try { sessionStorage.removeItem(APPLY_SCREENING_STORAGE_KEY); } catch { /* nothing to clear */ }
}

/** Staff view of an application invite (B6 / F-013). */
/**
 * The applications list envelope (B-435).
 *
 * `count` is the page length; `total` is how many match the same filter. They
 * are separate names because conflating them is what let the queue truncate
 * silently. `total: null` means the count FAILED — unknown. Render it as
 * unknown; never fall back to `count` and never to 0 (D-FB-13).
 */
/** The orderings `GET /api/admin/applications` offers (AP-41). */
export type ApplicationSort = 'newest' | 'days_in_state';

export interface ApplicationListMeta {
  count: number;
  total: number | null;
  limit: number;
  offset: number;
  // AP-41: the ordering the server ACTUALLY applied — echoed like `limit`, so
  // an ignored `?sort=` cannot leave the page claiming an order it lacks.
  // Optional: a server that predates this omits it.
  sort?: ApplicationSort;
  /** False when the age sort hit its scan cap and covers only a prefix. */
  sort_complete?: boolean;
  /**
   * #1182 — the chase bucket the server ACTUALLY applied, `null` when off.
   * Echoed like `sort` and for the same reason: a page must never claim a
   * worklist it is not showing.
   */
  documents?: ChaseBucket | null;
  /**
   * D-SE-24's chase set, as the SERVER holds it. The column labels itself from
   * the rows' own `documents_chase`, so TRF widening the set needs no frontend
   * change; this rides along so a caller can name the set without a second copy
   * of a program decision.
   */
  documents_kinds?: string[];
  /** #2234 — whether the carried-over filter was applied. */
  carried_over?: boolean;
}

/**
 * #416 — a freshly minted provider drop-off link.
 *
 * `link` carries the raw token, and this response is the ONLY place it ever
 * appears: the row stores a sha256 hash and nothing echoes the secret back.
 * The list route (#2160) returns ProviderUploadRequest below, never the link.
 */
export interface ProviderUploadTokenMint {
  id: string;
  /** Last 4 chars of the raw token — a display handle, never the token. */
  token_suffix: string;
  created_at: string;
  expires_at: string;
  expires_in_days: number;
  status: 'active' | 'used' | 'expired';
  /** #1425 — a REPLACEMENT link: the letter it brings in supersedes the one
   *  already on the application instead of being refused at the cap. */
  replace: boolean;
  link: string;
}

/**
 * #2160 — one provider request as GET /admin/applications/:id/provider-upload-tokens
 * returns it. Never the token or its hash; `status` uses the POST's words.
 */
export interface ProviderUploadRequest {
  id: string;
  /** Last 4 chars of the raw token — a display handle, never the token. */
  token_suffix: string | null;
  status: 'active' | 'used' | 'expired';
  created_at: string | null;
  expires_at: string | null;
  /** When the provider's letter came in through the link. */
  used_at: string | null;
  /** The staff user id who minted it. */
  requested_by: string | null;
  note: string | null;
  replace: boolean;
  source_document_id: string | null;
}

/**
 * A referral code (2026-10-28 redesign). Two kinds, told apart by the letter
 * in the code: `F` Family (a TRFer or board member hands it out) and `P`
 * Partner (a partner hospital hands it out). Every code opens any number of
 * applications; only Revoke stops one. `families` / `admitted` are the
 * applications that carry the code and, of those, the admitted ones.
 */
export type ReferralCodeKind = 'F' | 'P';

/** One person at a partner hospital who hands the code out. */
export interface ReferralContact {
  name: string;
  email: string | null;
  phone: string | null;
}

export interface ReferralCodeSummary {
  id: string;
  /** Display form, e.g. `TRF-F-6NPA-4GS`, or an older string as it was made. */
  code: string;
  kind: ReferralCodeKind;
  hospital_id: string | null;
  hospital_name: string | null;
  /** Family code: the person who hands it out. */
  referrer_name: string | null;
  /** Partner code: the contacts' names joined ("Anne Waiklund and Mary Mendoza"). */
  contact_name: string | null;
  /** Partner code: the social workers or nurse navigators, each with an optional email and phone. */
  contacts: ReferralContact[];
  note: string | null;
  /** Renewal Families: a family using it skips the pre-screen. */
  renewal: boolean;
  status: 'active' | 'revoked';
  families: number;
  /** 2026-11-02 (Melanie): how many families the code may open; null = no limit. */
  max_families?: number | null;
  /** Places used: every application with the code, drafts included, withdrawn ones not. */
  used?: number;
  /** Every place on the code is used. */
  full?: boolean;
  admitted: number;
  /** Ready-to-share link: `<FRONTEND_BASE_URL>/apply?code=<code>`. */
  link: string;
  created_at: string;
  created_by: string | null;
  /** The maker's first name. */
  created_by_name: string | null;
  updated_at: string | null;
  revoked_at: string | null;
}

export interface ReferralCodeCreatePayload {
  kind: ReferralCodeKind;
  hospital_id?: string | null;
  referrer_name?: string | null;
  contact_name?: string | null;
  contacts?: Array<{ name: string; email?: string | null; phone?: string | null }>;
  note?: string | null;
  renewal?: boolean;
  /** Absent: the kind's default (3 for Partner, none for Family); null: no limit. */
  max_families?: number | null;
}

export type ReferralCodeUpdatePayload = Omit<ReferralCodeCreatePayload, 'kind'>;

/** What DELETE /admin/referral-codes/:id answers once the row is gone. */
export interface ReferralCodeDeleted {
  id: string;
  deleted: true;
}

export type ReferralCodeBulkAction = 'revoke' | 'delete';

export interface ReferralCodeBulkResult {
  done: string[];
  skipped: Array<{
    id: string;
    code: string | null;
    reason: 'revoked' | 'has_families' | 'not_found';
    families?: number;
  }>;
}

/** A cycle's status, derived on the server from its dates and the clock (2026-10-31). */
export type ApplicationCycleStatus = 'unscheduled' | 'upcoming' | 'accepting' | 'closed';

/**
 * Application cycle (#1017) — one round of applications. Since 2026-10-31 a
 * cycle is its dates: it accepts applications from `opens_at` to `closes_at`,
 * and `status` is derived from them. Nobody opens or closes a cycle by hand.
 */
export interface ApplicationCycleSummary {
  id: string;
  key: string;
  label: string | null;
  status: ApplicationCycleStatus;
  /** Legacy stored state, kept on the wire for one release. Read `status`. */
  state?: string | null;
  /** How many applications are stamped with this cycle; null where the server did not count. */
  application_count: number | null;
  /** #3463: how many of those are renewing families. Only GET /cycles fills it. */
  renewal_count?: number | null;
  /** What a family reads when they try to submit after the deadline. */
  closed_notice: string;
  tail_length: number;
  /** Only populated by GET /cycles — the list view. Always 0 since 2026-10-28. */
  live_code_count?: number;
  created_at: string;
  /** #2198: 00:00 PT on the opening date. */
  opens_at: string | null;
  /** #2198: 23:59:59.999 PT on the deadline, or the instant End now was pressed. */
  closes_at: string | null;
  /** #3452: 00:00 PT on the "New families from" day; null when everyone opens on opens_at. */
  new_families_opens_at?: string | null;
  /** When staff pressed End now; null otherwise. */
  ended_at?: string | null;
  /** When waitlisted families were carried into this cycle (the first minute it accepted). */
  waitlist_carried_at?: string | null;
  /** #3092: the award letter's dates for this cycle; null when none is set. */
  award_terms?: CycleAwardTerms | null;
  /** The referral letter's per-cycle lines (2026-10-31); null when none is set. */
  referral_terms?: CycleReferralTerms | null;
}

/** The referral letter's two per-cycle lines: families per partner hospital, when decisions go out. */
export interface CycleReferralTerms {
  /** The cycle as the letter names it ("2027 Cycle I"); the label when null. */
  cycle_name: string | null;
  families_per_partner: number | null;
  decisions_notice: string | null;
}

/** Set, clear (null) or leave alone (absent) each referral letter line. */
export interface CycleReferralTermsPayload {
  cycle_name?: string | null;
  families_per_partner?: number | null;
  decisions_notice?: string | null;
}

/** A `{{field}}` a letter template can use; the platform fills it in per code. */
export interface LetterField {
  key: string;
  /** The staff words a missing value prints as ("[cycle deadline]"). */
  label: string;
  help: string;
}

/** A referral letter template (2026-11-01), on /trfer/referrals?view=letters. */
export interface LetterTemplate {
  id: string;
  name: string;
  /** The kind of code it is written for. */
  audience: ReferralCodeKind;
  subject: string;
  /** Letter markup (letterMarkup.ts). */
  body: string;
  /** New codes of this kind start on it. */
  is_default: boolean;
  source: 'standard' | 'blank' | 'copy' | 'upload';
  source_file: string | null;
  fields: string[];
  created_at: string | null;
  created_by_name: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
}

/** A picture as the app draws it: a data URL at its drawn size. */
export interface LetterPicture {
  src: string;
  width: number;
  height: number;
  /** The picture that ships with the platform (not uploaded by TRF). */
  built_in?: boolean;
}

/** One staff member's signature lines (2026-11-02). */
export interface LetterSignature {
  title: string;
  phone: string;
}

/** The shared parts of the letter signature (2026-11-02; no header, no closing picture). */
export interface Letterhead {
  /** The signature's dove. */
  logo: LetterPicture | null;
  /** Lines every signature carries (the mailing address). */
  signature_lines: string;
  /** The small print under the signature. */
  notice: string;
  /** The signed-in TRFer's own signature lines. */
  my_signature?: LetterSignature;
  updated_at: string | null;
}

export type LetterPictureKind = 'logo';

/** Someone who can sign a letter: a TRF staff account, with their signature lines. */
export interface LetterSigner extends LetterSignature {
  user_id: string;
  name: string | null;
  email: string | null;
}

/** What closes the email, once, under the last letter; null when the Signature checkbox is off. */
export interface LetterFrame {
  signature: {
    logo: LetterPicture | null;
    name: string | null;
    title: string | null;
    contact: string;
    lines: string[];
  } | null;
  notice: string | null;
}

/** One letter in a code's email: the hospital's (main) or the family letter it forwards. */
export interface ReferralLetterPart {
  key: 'main' | 'family';
  title: string;
  markup: string;
  edited: { at: string | null; by_name: string | null; changed_since: string[] } | null;
}

export interface LetterTemplateList {
  letterhead: Letterhead;
  templates: LetterTemplate[];
  fields: LetterField[];
  /** TRF's standard letters, to start a new template from. */
  standard: Array<{ audience: ReferralCodeKind; name: string; subject: string; body: string }>;
}

export interface LetterTemplatePayload {
  name: string;
  audience: ReferralCodeKind;
  subject: string;
  body: string;
}

/** POST /admin/letter-templates/import — an uploaded letter as a draft, not yet saved. */
export interface LetterTemplateDraft extends LetterTemplatePayload {
  replaced: Array<{ field: string; label: string; original: string }>;
  fields_marked: boolean;
  /** Why the fields were not marked, when they were not. */
  note: string | null;
  source_file: string;
}

export interface LetterPreview {
  frame: LetterFrame;
  subject: string;
  html: string;
  text: string;
  markup: string;
  gaps: string[];
  /** Filled in for a made-up code. */
  sample: boolean;
  cycle_label: string | null;
}

/** GET /admin/referral-codes/:id/letter — the letter filled in for one code. */
export interface ReferralLetter {
  subject: string;
  text: string;
  html: string;
  /** The filled-in letter as markup — what the inline editor opens on. */
  markup: string;
  /** 2026-11-02: each letter in the email, in order (a Partner code: the hospital's, then the family letter). */
  parts: ReferralLetterPart[];
  /** The signature drawn once, under the last letter. */
  frame: LetterFrame;
  /** The Signature checkbox. */
  footer: boolean;
  /** Who the letter is signed as. */
  signer_id: string;
  /** A Partner code's family letter (id 'none' when left out); null for a Family code. */
  family_template: { id: string | null; name: string; audience: ReferralCodeKind } | null;
  /** The template the letter comes from (id null: TRF's built-in standard letter). */
  template: { id: string | null; name: string; audience: ReferralCodeKind };
  /** Set when a TRFer edited this code's letter by hand. */
  edited: {
    at: string | null;
    by_name: string | null;
    /** The values that changed in Settings (or on the code) since the edit, in staff words. */
    changed_since: string[];
  } | null;
  kind: ReferralCodeKind;
  /** What the letter is missing, in staff words; Send is refused while any remains. */
  gaps: string[];
  cycle_label: string | null;
  /** The code's contacts that have an email — the only people Send can reach. */
  recipients: Array<{ name: string | null; email: string }>;
  /** Everyone on the code, with or without an email. */
  contacts: ReferralContact[];
  revoked: boolean;
  /** The last sends, newest first. */
  sent: Array<{ at: string; to: string[]; by_name: string | null }>;
}

/** #2198: set (YYYY-MM-DD), clear (null) or leave alone (absent) each date. */
export interface ApplicationCycleDatesPayload {
  opens_on?: string | null;
  closes_on?: string | null;
  /** #3452: the "New families from" day. */
  new_families_on?: string | null;
}

/**
 * #3092: a cycle's award terms as the server returns them. Each field is null
 * when unset. `acceptance_deadline` is 23:59:59.999 PT on the due day;
 * `onboarding_meeting_at` is the meeting's instant.
 */
export interface CycleAwardTerms {
  letter_date: string | null;
  first_payment_month: string | null;
  expiration_month: string | null;
  acceptance_deadline: string | null;
  onboarding_meeting_at: string | null;
  onboarding_meeting_link: string | null;
}

/**
 * #3092: set, clear (null) or leave alone (absent) each award term. Dates are
 * Pacific YYYY-MM-DD, months YYYY-MM, the meeting a Pacific YYYY-MM-DDTHH:MM.
 */
export type CycleAwardTermsPayload = Partial<Record<keyof CycleAwardTerms, string | null>>;

/** Create payload for a new application cycle (#1017) — it accepts nothing until its dates are set. */
export interface ApplicationCycleCreatePayload {
  key: string;
  label?: string;
  closed_notice?: string;
}

/**
 * A partner hospital record (#1018) — the structured typeahead list behind
 * the /apply referral "slow path". Trimmed to exactly the four public fields
 * the backend returns (`backend/src/routes/partnerHospitals.js`): no
 * `created_by`, no timestamps, no `active` flag.
 */
export interface PartnerHospitalSummary {
  id: string;
  name: string;
  city: string;
  state: string;
}

/**
 * The full admin row (#1151) — what `PartnerHospital.listAll()` returns, i.e.
 * the raw Mongo document including the `active` flag the public summary hides.
 */
export interface PartnerHospitalAdminRow {
  _id: string;
  name: string;
  city: string | null;
  state: string | null;
  active: boolean;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PartnerHospitalEdit {
  name?: string;
  city?: string | null;
  state?: string | null;
}

/**
 * How a reimbursement claim gets paid (#419).
 *
 * The first three are bookkeeping: a human already moved the money and is
 * recording it. `billcom` asks BILL Spend & Expense to create an
 * AWAITING_APPROVAL reimbursement — a BILL admin releases the funds, never us.
 */
export type PayoutMethod = 'ach' | 'check' | 'manual' | 'billcom';

/**
 * The 202 answer to a `billcom` submission. BILL did not confirm the write, so
 * it may or may not have landed; the claim sits in `paying` until a human
 * reconciles it. Modelled in the type so a caller cannot mistake it for an
 * ordinary success — or, worse, treat it as a failure worth retrying.
 */
export interface PendingReconciliation {
  status: 'pending_reconciliation';
  message: string;
}

export type MarkPaidResult = ApiResponse<unknown> | PendingReconciliation;

/** BILL environments. A mapping is scoped to exactly one (#419). */
export type BillEnv = 'sandbox' | 'production';

/**
 * How far a parent has got through BILL's own identity/bank-link flow, as
 * asserted by a TRFer. BILL publishes no completion signal, so every one of
 * these values is set by a human action — nothing is detected (#445).
 */
export type BillOnboardingState = 'not_invited' | 'invited' | 'confirmed' | 'declined';

/** A row of `bill_identities`: "money for this TRF entity goes to that BILL one". */
/**
 * `rate_limited` is deliberately its own bucket, not folded into `pending`:
 * BILL refusing to answer is an UNKNOWN, and reporting it as "not released
 * yet" states a fact we do not have.
 */
export interface BillCheckResult {
  paid: string[];
  denied: string[];
  pending: string[];
  rate_limited?: string[];
}

export interface BillIdentityRow {
  _id: string;
  trf_entity_type: 'family' | 'user';
  trf_id: string;
  bill_kind: 'budget' | 'user';
  bill_uuid: string;
  bill_env: BillEnv;
  label: string | null;
  linked_at: string;
  bill_onboarding_state?: BillOnboardingState;
  onboarding_updated_at?: string | null;
}

export interface BillIdentityLinkPayload {
  trf_entity_type: 'family' | 'user';
  trf_id: string;
  bill_kind: 'budget' | 'user';
  bill_uuid: string;
  bill_env: BillEnv;
  label?: string;
}

export interface BillOnboardingResult {
  id: string;
  uuid: string;
  state: BillOnboardingState;
}

/**
 * API Service
 */
/** One approved grant a reimbursement may draw from (staff queue, 2026-09-14). */
export interface ClaimQueueGrantOption { _id: string; type: string; amount_approved_cents: number | null }
export type ClaimQueueGrantsByFamily = Record<string, ClaimQueueGrantOption[]>;

/** Drop unset query values so the server never reads `?country=` as a filter. */
function cleanParams(params: object): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Record<string, string | number>;
}

/**
 * Final review M3 / walk W1 (2026-09-30) — the reviewer's LOCAL calendar date, as YYYY-MM-DD,
 * for a committee download's `as_of`. Without it the server takes today in UTC, which after
 * 8 PM Eastern is tomorrow: the cover's generated date, the default title's month on a month's
 * last evening, a child's age on a birthday eve and the audit row's `as_of` all moved a day.
 * Local getters on purpose — `toISOString()` is the UTC date, the very thing being fixed.
 */
function localIsoDate(now: Date = new Date()): string {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const COMMITTEE_PRESENTATIONS_PATH = '/admin/committee-presentations';
const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * Task 14 (2026-09-30) — GET a committee PDF as a blob, save it, and report the server's
 * overflow header. Shared by the one-pager and the package download: same blob +
 * refusal-decoding path `downloadCommitteePacket` already uses (see its own comment above).
 */
/**
 * The file name a response's `Content-Disposition` gives (`filename="…"`), else null — so a
 * download saves under the server's own dated name (QA B7, 2026-10-26).
 */
export function dispositionFilename(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const m = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  const name = m?.[1]?.trim();
  return name && !/[\\/]/.test(name) ? name : null;
}

async function downloadCommitteePdf(
  url: string,
  params: Record<string, string | undefined>,
  filename: string,
  { serverName = false, type = 'application/pdf' }: { serverName?: boolean; type?: string } = {},
): Promise<{ overflow: string | null }> {
  const res = await apiClient
    .get<Blob>(url, {
      responseType: 'blob',
      params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)),
    })
    .catch(async (error: unknown) => { throw await decodeBlobErrorBody(error); });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = (serverName ? dispositionFilename(res.headers?.['content-disposition']) : null) ?? filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
  return { overflow: (res.headers?.['x-committee-overflow'] as string | undefined) ?? null };
}

/**
 * #2391: a parent's data-export or data-deletion request, as the settings
 * routes report it (backend/src/routes/settings.js `handleDataRequest`,
 * models/DataRequest.js). `already_open` is true when the server found an
 * open request of that kind and recorded nothing new.
 */
export type DataRequestKind = 'export' | 'deletion';
export type DataRequestStatus = 'open' | 'handled';
export interface DataRequestResult {
  request_id: string;
  kind: DataRequestKind;
  status: DataRequestStatus;
  already_open?: boolean;
}
/** #2391: one open request, as `GET /settings/consent` hydrates it. */
export interface DataRequestSummary {
  request_id: string;
  kind: DataRequestKind;
  status: DataRequestStatus;
  requested_at: string | null;
}
/**
 * #2391: the parent's OPEN requests by kind; a kind with no open request is
 * null or absent. Optional on the consent read until the server sends it.
 */
export type ConsentDataRequests = Partial<Record<DataRequestKind, DataRequestSummary | null>>;

/**
 * #2390 (D-2390): the account's closure state, read by `GET /account/close`
 * and returned by `POST` and `DELETE` on the same path. Both dates are null
 * when no closure is scheduled. `already_scheduled` is true when a repeat
 * POST kept the existing date rather than moving it.
 */
export interface AccountClosureStatus {
  grace_days: number;
  closure_requested_at: string | null;
  closure_scheduled_at: string | null;
  already_scheduled?: boolean;
}

/** #2440: the step key the family is ON, `[a-z0-9_.-]{1,64}`. */
export interface ResumeStepResult {
  resume_step: string | null;
}

/**
 * #2756 item 1: a section save that survives the tab closing.
 *
 * The browser cancels an in-flight XHR (axios) when the page unloads, so the
 * wizard's pagehide flush could lose the last typed answer on a desktop tab
 * close. `fetch` with `keepalive: true` is allowed to finish after the page is
 * gone. It carries the same bearer token and language header as `apiClient`,
 * and the refresh cookie via `credentials: 'include'`. It has no 401 refresh:
 * a refused save rejects, and the wizard keeps those paths dirty for the next
 * save, exactly as it does for any failed save. A body over the keepalive
 * budget goes through the ordinary save instead (see below).
 */
const KEEPALIVE_BODY_LIMIT_BYTES = 60 * 1024;
async function patchSectionKeepalive(
  path: string,
  section: string,
  values: Record<string, unknown>,
): Promise<ApiResponse<FamilyApplication>> {
  const body = JSON.stringify({ section, values });
  // #2818 review: the browser's keepalive budget is 64 KB, shared by every
  // keepalive request in flight; a larger body is refused outright. Over
  // ~60 KB, send the ordinary save instead: it may not outlive the tab, but it
  // is never refused for its size.
  if (new TextEncoder().encode(body).length > KEEPALIVE_BODY_LIMIT_BYTES) {
    const response = await apiClient.patch<ApiResponse<FamilyApplication>>(path, { section, values });
    return response.data;
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await authStorage.get('auth_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const locale = requestLocale();
  if (locale) headers[REQUEST_LOCALE_HEADER] = locale;
  const response = await fetch(`${API_BASE_URL}/api${path}`, {
    method: 'PATCH',
    keepalive: true,
    credentials: 'include',
    headers,
    body,
  });
  if (!response.ok) throw new Error(`section save failed (${response.status})`);
  return (await response.json()) as ApiResponse<FamilyApplication>;
}

export const api = {
  /**
   * Authentication
   */
  auth: {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
      const response = await apiClient.post<LoginResponse>('/auth/login', data);
      return response.data;
    },

    register: async (data: RegisterRequest): Promise<ApiResponse<RegisterResponse>> => {
      const response = await apiClient.post<ApiResponse<RegisterResponse>>('/auth/register', data);
      return response.data;
    },

    logout: async (params?: { device_token?: string }): Promise<ApiResponse> => {
      // device_token (when present) lets the backend revoke that push device
      // server-side as part of logout. See M3.5 + B-064.
      const response = await apiClient.post<ApiResponse>('/auth/logout', params ?? {});
      return response.data;
    },

    getCurrentUser: async (): Promise<ApiResponse<{ user: User }>> => {
      const response = await apiClient.get<ApiResponse<{ user: User }>>('/auth/me');
      return response.data;
    },

    forgotPassword: async (email: string): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>('/auth/forgot-password', { email });
      return response.data;
    },

    resetPassword: async (token: string, newPassword: string): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>('/auth/reset-password', {
        token,
        password: newPassword,
      });
      return response.data;
    },

    requestMagicLink: async (email: string): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>('/auth/magic-link', { email });
      return response.data;
    },

    verifyMagicLink: async (token: string, rememberMe = false, trustedDeviceToken?: string | null): Promise<LoginResponse> => {
      // rememberMe sets the web refresh-cookie lifetime; native ignores it.
      const response = await apiClient.post<LoginResponse>('/auth/magic-link/verify', { token, rememberMe, ...(trustedDeviceToken ? { trusted_device_token: trustedDeviceToken } : {}) });
      return response.data;
    },

    requestSmsCode: async (email: string): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>('/auth/sms-code', { email });
      return response.data;
    },

    verifySmsCode: async (email: string, code: string, rememberMe = false, trustedDeviceToken?: string | null): Promise<LoginResponse> => {
      // rememberMe sets the web refresh-cookie lifetime; native ignores it.
      const response = await apiClient.post<LoginResponse>('/auth/sms-code/verify', { email, code, rememberMe, ...(trustedDeviceToken ? { trusted_device_token: trustedDeviceToken } : {}) });
      return response.data;
    },

    refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
      const response = await apiClient.post<ApiResponse<{ token: string }>>('/auth/refresh');
      return response.data;
    },

    updateProfile: async (data: {
      first_name?: string;
      last_name?: string;
      phone?: string;
      personal_email?: string;
      bio?: string;
      timezone?: string;
      /** The UI language — `User.preferences.language` (#2109 rulings brief B3). */
      language?: LanguageCode;
      location?: {
        address?: string;
        city?: string;
        state?: string;
        zip_code?: string;
      };
    }): Promise<ApiResponse<{ user: User }>> => {
      const response = await apiClient.patch<ApiResponse<{ user: User }>>('/auth/profile', data);
      return response.data;
    },

    changePassword: async (current_password: string, new_password: string): Promise<ApiResponse> => {
      const response = await apiClient.put<ApiResponse>('/auth/change-password', {
        current_password,
        new_password,
      });
      return response.data;
    },

    switchRole: async (role: User['role']): Promise<LoginResponse> => {
      const response = await apiClient.post<LoginResponse>('/auth/switch-role', { role });
      return response.data;
    },

    applySignup: async (data: { email: string; password: string; first_name: string; rememberMe?: boolean }): Promise<LoginResponse> => {
      const r = await apiClient.post<LoginResponse>('/auth/apply-signup', data);
      return r.data;
    },

    verifyEmail: async (token: string): Promise<ApiResponse> => {
      const r = await apiClient.post<ApiResponse>('/auth/verify-email', { token });
      return r.data;
    },

    sendEmailVerification: async (): Promise<ApiResponse> => {
      const r = await apiClient.post<ApiResponse>('/auth/send-email-verification');
      return r.data;
    },
  },

  /**
   * MFA
   */
  mfa: {
    verify: async (data: MfaVerifyRequest): Promise<ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken?: string }; trusted_device_token?: string }>> => {
      const response = await apiClient.post('/auth/mfa/verify', data);
      return response.data;
    },
    resend: async (mfaToken: string): Promise<ApiResponse> => {
      const response = await apiClient.post('/auth/mfa/resend', { mfa_token: mfaToken });
      return response.data;
    },
    status: async (): Promise<ApiResponse<MfaStatusResponse>> => {
      const response = await apiClient.get('/auth/mfa/status');
      return response.data;
    },
    enrollStart: async (method: 'email' | 'sms'): Promise<ApiResponse<MfaEnrollStartResponse>> => {
      const response = await apiClient.post('/auth/mfa/enroll/start', { method });
      return response.data;
    },
    enrollVerify: async (method: 'email' | 'sms', code: string): Promise<ApiResponse> => {
      const response = await apiClient.post('/auth/mfa/enroll/verify', { method, code });
      return response.data;
    },
    disable: async (password: string): Promise<ApiResponse> => {
      const response = await apiClient.delete('/auth/mfa/disable', { data: { password } });
      return response.data;
    },
  },

  /**
   * Consent & Settings
   */
  consent: {
    get: async (): Promise<ApiResponse<{
      consent: ConsentSettings;
      notifications: NotificationSettings;
      implicit_handoff: ImplicitHandoffConsent;
      outbound_consent?: Record<string, boolean>;
      /** #2391: open export/deletion requests, so a refresh keeps the pending state. */
      data_requests?: ConsentDataRequests;
    }>> => {
      const response = await apiClient.get('/settings/consent');
      return response.data;
    },
    update: async (data: Partial<ConsentSettings & {
      notifications_email?: boolean;
      notifications_push?: boolean;
      push_categories?: Partial<PushCategorySettings>;
      implicit_handoff?: Partial<ImplicitHandoffConsent>;
      notification_prefs_polls?: boolean;
    }>): Promise<ApiResponse<{
      consent: ConsentSettings;
      notifications: NotificationSettings;
      implicit_handoff: ImplicitHandoffConsent | null;
    }>> => {
      const response = await apiClient.patch('/settings/consent', data);
      return response.data;
    },
    requestExport: async (): Promise<ApiResponse<DataRequestResult>> => {
      const response = await apiClient.post<ApiResponse<DataRequestResult>>('/settings/data-export');
      return response.data;
    },
    requestDeletion: async (): Promise<ApiResponse<DataRequestResult>> => {
      const response = await apiClient.post<ApiResponse<DataRequestResult>>('/settings/data-deletion');
      return response.data;
    },
    // Re-acceptance of updated legal documents (clears a re-consent block).
    accept: async (documents: string[]): Promise<ApiResponse> => {
      const response = await apiClient.post('/consent/accept', { documents });
      return response.data;
    },
    // Has the user accepted the CURRENT version of a disclosure document?
    disclosureStatus: async (documentId: string): Promise<ApiResponse<{ accepted: boolean }>> => {
      const response = await apiClient.get(`/consent/disclosure/${documentId}`);
      return response.data;
    },
    // Record explicit acceptance of a disclosure document (flow: 'disclosure').
    recordDisclosure: async (documentId: string): Promise<ApiResponse<{ accepted: boolean }>> => {
      const response = await apiClient.post('/consent/disclosure', { document_id: documentId });
      return response.data;
    },
  },

  account: {
    close: async (): Promise<ApiResponse<{ grace_days: number; closure_scheduled_at: string } & Partial<AccountClosureStatus>>> => {
      const response = await apiClient.post('/account/close', {});
      return response.data;
    },
    /** #2390: is a closure scheduled? Read on /account/close and Settings → Account. */
    closeStatus: async (): Promise<ApiResponse<AccountClosureStatus>> => {
      const response = await apiClient.get<ApiResponse<AccountClosureStatus>>('/account/close');
      return response.data;
    },
    /** #2390 (D-2390): cancel a scheduled closure in the app; both dates come back null. */
    cancelClose: async (): Promise<ApiResponse<AccountClosureStatus>> => {
      const response = await apiClient.delete<ApiResponse<AccountClosureStatus>>('/account/close');
      return response.data;
    },
  },

  /**
   * Legal documents (Spec 12). Public endpoints — no auth required for GETs.
   */
  legal: {
    index: async (): Promise<ApiResponse<LegalDocMeta[]>> => {
      const response = await apiClient.get('/legal');
      return response.data;
    },
    get: async (doc: string): Promise<ApiResponse<LegalDocumentFull>> => {
      const response = await apiClient.get(`/legal/${doc}`);
      return response.data;
    },
    history: async (doc: string): Promise<ApiResponse<LegalDocMeta[]>> => {
      const response = await apiClient.get(`/legal/${doc}/history`);
      return response.data;
    },
    version: async (doc: string, version: string): Promise<ApiResponse<LegalDocumentFull>> => {
      const response = await apiClient.get(`/legal/${doc}/v/${version}`);
      return response.data;
    },
    myStatus: async (): Promise<ApiResponse<{ status: UserLegalStatus | null; events: ConsentEventRow[] }>> => {
      const response = await apiClient.get('/settings/legal');
      return response.data;
    },
  },

  /**
   * Device Tokens (M3.9)
   * Register / list / revoke APNs / FCM / web push tokens for the authenticated
   * user. Backend routes: backend/src/routes/devices.js
   */
  devices: {
    register: async (data: {
      platform: 'ios' | 'android' | 'web';
      token: string;
      app_version?: string;
    }): Promise<ApiResponse<{ device: { token: string } }>> => {
      const response = await apiClient.post('/devices/register', data);
      return response.data;
    },
    list: async (): Promise<ApiResponse<{ devices: Array<{
      platform: string;
      token: string;
      app_version: string | null;
      last_seen: string;
    }> }>> => {
      const response = await apiClient.get('/devices');
      return response.data;
    },
    delete: async (token: string): Promise<ApiResponse> => {
      const response = await apiClient.delete(`/devices/${encodeURIComponent(token)}`);
      return response.data;
    },
  },

  /**
   * Tasks
   */
  tasks: {
    getAll: async (params?: {
      start_date?: string;
      end_date?: string;
      status?: string;
      category?: string;
    }): Promise<ApiResponse<Task[]>> => {
      const response = await apiClient.get<ApiResponse<Task[]>>('/tasks', { params });
      return response.data;
    },

    getById: async (id: string): Promise<ApiResponse<Task>> => {
      const response = await apiClient.get<ApiResponse<Task>>(`/tasks/${id}`);
      return response.data;
    },

    create: async (data: CreateTaskRequest): Promise<ApiResponse<Task>> => {
      const response = await apiClient.post<ApiResponse<Task>>('/tasks', data);
      return response.data;
    },

    update: async (id: string, data: UpdateTaskRequest): Promise<ApiResponse<Task>> => {
      const response = await apiClient.patch<ApiResponse<Task>>(`/tasks/${id}`, data);
      return response.data;
    },

    complete: async (id: string, notes?: string): Promise<ApiResponse<Task>> => {
      const response = await apiClient.post<ApiResponse<Task>>(`/tasks/${id}/complete`, { notes });
      return response.data;
    },

    cancel: async (id: string, reason?: string): Promise<ApiResponse<Task>> => {
      const response = await apiClient.post<ApiResponse<Task>>(`/tasks/${id}/cancel`, { reason });
      return response.data;
    },

    cancelRecurrence: async (recurrenceId: string, from?: string): Promise<ApiResponse<{ cancelled_count: number }>> => {
      const response = await apiClient.delete<ApiResponse<{ cancelled_count: number }>>(
        `/tasks/recurrence/${recurrenceId}`,
        { params: from ? { from } : undefined }
      );
      return response.data;
    },
  },

  /**
   * Notifications
   */
  notifications: {
    getAll: async (): Promise<ApiResponse<Notification[]>> => {
      const response = await apiClient.get<ApiResponse<Notification[]>>('/notifications');
      return response.data;
    },

    markAsRead: async (id: string): Promise<ApiResponse<Notification>> => {
      const response = await apiClient.post<ApiResponse<Notification>>(`/notifications/${id}/read`);
      return response.data;
    },

    getUnreadCount: async (): Promise<ApiResponse<{ count: number }>> => {
      const response = await apiClient.get<ApiResponse<{ count: number }>>('/notifications/unread-count');
      return response.data;
    },

    markAllAsRead: async (): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>('/notifications/read-all');
      return response.data;
    },

    // Audited outbound-care opt-in/out. sms_opt_in / email_marketing_opt_in
    // were removed (#642, D-7): the backend no longer accepts them — each
    // capture flow is rebuilt with its first sender.
    update: async (patch: { outbound_consent?: Record<string, boolean> }): Promise<ApiResponse<{ updated: Record<string, boolean> }>> => {
      const r = await apiClient.patch('/settings/notifications', patch);
      return r.data;
    },
  },

  /**
   * The parent Resources page (`/api/circle`). The contacts address book and
   * its four methods went with the People page (wave w1023c L2).
   */
  circle: {
    getResources: async (): Promise<ApiResponse<Resource[]>> => {
      const response = await apiClient.get<ApiResponse<Resource[]>>('/circle/resources');
      return response.data;
    },
    /** "This didn't work" (w1023c L3): 201; 409 when this family reported it in the last 7 days. */
    reportResource: async (id: string, note?: string | null): Promise<ApiResponse> => {
      const response = await apiClient.post<ApiResponse>(`/circle/resources/${id}/report`, { note: note || null });
      return response.data;
    },
  },

  /**
   * Staff resource management — curated support resources (TRFer/admin only).
   */
  trferResources: {
    list: async (params?: { category?: ResourceCategory; status?: 'draft' | 'published' }): Promise<ApiResponse<Resource[]>> => {
      const qs = new URLSearchParams();
      if (params?.category) qs.set('category', params.category);
      if (params?.status) qs.set('status', params.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const response = await apiClient.get<ApiResponse<Resource[]>>(`/trfer/resources${suffix}`);
      return response.data;
    },
    create: async (data: ResourceWriteRequest): Promise<ApiResponse<Resource>> => {
      const response = await apiClient.post<ApiResponse<Resource>>('/trfer/resources', data);
      return response.data;
    },
    update: async (id: string, data: Partial<ResourceWriteRequest>): Promise<ApiResponse<Resource>> => {
      const response = await apiClient.patch<ApiResponse<Resource>>(`/trfer/resources/${id}`, data);
      return response.data;
    },
    remove: async (id: string): Promise<ApiResponse> => {
      const response = await apiClient.delete<ApiResponse>(`/trfer/resources/${id}`);
      return response.data;
    },
    /** "Mark checked" (w1023c L3): stamps last_checked_at = now and clears the reports. */
    markChecked: async (id: string): Promise<ApiResponse<Resource>> => {
      const response = await apiClient.post<ApiResponse<Resource>>(`/trfer/resources/${id}/mark-checked`);
      return response.data;
    },
    /** Preview what a family with the given country + phase + state would see on /circle. */
    preview: async (params: { country?: ResourceCountry; treatment_phase?: ResourceTreatmentPhase; state?: ResourceState }): Promise<ApiResponse<Resource[]>> => {
      const qs = new URLSearchParams();
      if (params.country) qs.set('country', params.country);
      if (params.treatment_phase) qs.set('treatment_phase', params.treatment_phase);
      if (params.state) qs.set('state', params.state);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const response = await apiClient.get<ApiResponse<Resource[]>>(`/trfer/resources/preview${suffix}`);
      return response.data;
    },
  },

  /**
   * Chat
   */
  chat: {
    sendMessage: async (message: string): Promise<ApiResponse<ChatMessage>> => {
      const response = await apiClient.post<ApiResponse<ChatMessage>>('/chat/message', { message });
      return response.data;
    },
  },

  /**
   * Family onboarding
   */
  families: {
    getMine: async (): Promise<ApiResponse<FamilyMine>> => {
      const response = await apiClient.get<ApiResponse<FamilyMine>>('/families/mine');
      return response.data;
    },
    updateOnboarding: async (data: UpdateOnboardingRequest): Promise<ApiResponse<void>> => {
      const response = await apiClient.patch<ApiResponse<void>>('/families/mine/onboarding', data);
      return response.data;
    },
    updatePatient: async (data: UpdatePatientRequest): Promise<ApiResponse<void>> => {
      const response = await apiClient.patch<ApiResponse<void>>('/families/mine/patient', data);
      return response.data;
    },
    updateAutoTaskPreferences: async (
      data: Partial<{ enabled: boolean; rules: Partial<{ refill_prep: boolean; post_chemo_recovery: boolean; appointment_prep: boolean }> }>,
    ): Promise<ApiResponse<void>> => {
      const response = await apiClient.patch<ApiResponse<void>>('/families/mine/auto-task-preferences', data);
      return response.data;
    },
    updateDashboardPreferences: async (prefs: Partial<DashboardPreferences>): Promise<ApiResponse<void>> => {
      const response = await apiClient.patch<ApiResponse<void>>('/families/mine/dashboard-preferences', prefs);
      return response.data;
    },
  },

  /**
   * Medical
   */
  medical: {
    appointments: {
      getAll: async (upcoming = false): Promise<ApiResponse<MedicalAppointment[]>> => {
        const response = await apiClient.get<ApiResponse<MedicalAppointment[]>>('/medical/appointments', { params: { upcoming } });
        return response.data;
      },
      create: async (data: CreateAppointmentRequest): Promise<ApiResponse<MedicalAppointment>> => {
        const response = await apiClient.post<ApiResponse<MedicalAppointment>>('/medical/appointments', data);
        return response.data;
      },
      update: async (id: string, data: Partial<CreateAppointmentRequest>): Promise<ApiResponse<MedicalAppointment>> => {
        const response = await apiClient.put<ApiResponse<MedicalAppointment>>(`/medical/appointments/${id}`, data);
        return response.data;
      },
      updateStatus: async (id: string, status: MedicalAppointment['status']): Promise<ApiResponse<MedicalAppointment>> => {
        const response = await apiClient.patch<ApiResponse<MedicalAppointment>>(`/medical/appointments/${id}/status`, { status });
        return response.data;
      },
      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(`/medical/appointments/${id}`);
        return response.data;
      },
    },
    medications: {
      getAll: async (): Promise<ApiResponse<Medication[]>> => {
        const response = await apiClient.get<ApiResponse<Medication[]>>('/medical/medications');
        return response.data;
      },
      create: async (data: Partial<Medication>): Promise<ApiResponse<Medication>> => {
        const response = await apiClient.post<ApiResponse<Medication>>('/medical/medications', data);
        return response.data;
      },
      update: async (id: string, data: Partial<Medication>): Promise<ApiResponse<Medication>> => {
        const response = await apiClient.put<ApiResponse<Medication>>(`/medical/medications/${id}`, data);
        return response.data;
      },
      toggle: async (id: string): Promise<ApiResponse<Medication>> => {
        const response = await apiClient.patch<ApiResponse<Medication>>(`/medical/medications/${id}/toggle`);
        return response.data;
      },
      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(`/medical/medications/${id}`);
        return response.data;
      },
    },
    /**
     * Dose events (#846). A dose is only ever `taken`/`skipped` — an explicit
     * assertion, never inferred. Logging the same (date, slot) again replaces
     * the earlier assertion (backend upsert); `undo` deletes the event so the
     * slot reverts to its derived pending/missed state. Retroactive logging
     * for past dates is accepted; future dates are rejected by the API.
     * Family-wide range read; callers (e.g. the #847 medication detail view)
     * filter by medication_id.
     */
    doses: {
      getRange: async (start: string, end: string): Promise<ApiResponse<MedicationDose[]>> => {
        const response = await apiClient.get<ApiResponse<MedicationDose[]>>('/medical/medications/doses', { params: { start, end } });
        return response.data;
      },
      log: async (medicationId: string, data: LogDoseRequest): Promise<ApiResponse<MedicationDose>> => {
        const response = await apiClient.post<ApiResponse<MedicationDose>>(`/medical/medications/${medicationId}/doses`, data);
        return response.data;
      },
    },
    questions: {
      getAll: async (params?: { appointment?: string; answered?: boolean }): Promise<ApiResponse<DoctorQuestion[]>> => {
        const response = await apiClient.get<ApiResponse<DoctorQuestion[]>>('/medical/questions', { params });
        return response.data;
      },
      create: async (data: Partial<DoctorQuestion>): Promise<ApiResponse<DoctorQuestion>> => {
        const response = await apiClient.post<ApiResponse<DoctorQuestion>>('/medical/questions', data);
        return response.data;
      },
      update: async (id: string, data: Partial<DoctorQuestion>): Promise<ApiResponse<DoctorQuestion>> => {
        const response = await apiClient.put<ApiResponse<DoctorQuestion>>(`/medical/questions/${id}`, data);
        return response.data;
      },
      markAnswered: async (id: string, answerNotes?: string): Promise<ApiResponse<DoctorQuestion>> => {
        const response = await apiClient.patch<ApiResponse<DoctorQuestion>>(`/medical/questions/${id}/answered`, { answer_notes: answerNotes });
        return response.data;
      },
      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(`/medical/questions/${id}`);
        return response.data;
      },
    },
    symptoms: {
      getAll: async (params?: { type?: string; severity?: string; medication?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<SymptomEntry[]>> => {
        const response = await apiClient.get<ApiResponse<SymptomEntry[]>>('/medical/symptoms', { params });
        return response.data;
      },
      create: async (data: Partial<SymptomEntry>): Promise<ApiResponse<SymptomEntry>> => {
        const response = await apiClient.post<ApiResponse<SymptomEntry>>('/medical/symptoms', data);
        return response.data;
      },
      update: async (id: string, data: Partial<SymptomEntry>): Promise<ApiResponse<SymptomEntry>> => {
        const response = await apiClient.put<ApiResponse<SymptomEntry>>(`/medical/symptoms/${id}`, data);
        return response.data;
      },
      delete: async (id: string): Promise<ApiResponse<void>> => {
        const response = await apiClient.delete<ApiResponse<void>>(`/medical/symptoms/${id}`);
        return response.data;
      },
      export: async (params?: { start_date?: string; end_date?: string }): Promise<ApiResponse<SymptomEntry[]>> => {
        const response = await apiClient.get<ApiResponse<SymptomEntry[]>>('/medical/symptoms/export', { params });
        return response.data;
      },
    },
  },

  /**
   * Finances (expenses)
   */

  /**
   * Community
   */
  community: {
    posts: {
      getAll: async (params?: CommunityFeedParams): Promise<PaginatedResponse<CommunityPost>> => {
        const response = await apiClient.get<PaginatedResponse<CommunityPost>>('/community/posts', { params });
        return response.data;
      },
      getMine: async (): Promise<ApiResponse<CommunityPost[]>> => {
        const response = await apiClient.get<ApiResponse<CommunityPost[]>>('/community/posts/mine');
        return response.data;
      },
      create: async (data: { body: string; title?: string | null; anonymous?: boolean }): Promise<ApiResponse<CommunityPost>> => {
        const response = await apiClient.post<ApiResponse<CommunityPost>>('/community/posts', data);
        return response.data;
      },
      react: async (id: string, reaction: string): Promise<ApiResponse<CommunityPost>> => {
        const response = await apiClient.post<ApiResponse<CommunityPost>>(`/community/posts/${id}/react`, { reaction });
        return response.data;
      },
      unreact: async (id: string, reaction: string): Promise<ApiResponse<CommunityPost>> => {
        const response = await apiClient.delete<ApiResponse<CommunityPost>>(`/community/posts/${id}/react`, { data: { reaction } });
        return response.data;
      },
    },
    moderation: {
      remove: async (id: string): Promise<ApiResponse<CommunityPost>> => {
        const response = await apiClient.post<ApiResponse<CommunityPost>>(`/trfer/community/posts/${id}/remove`);
        return response.data;
      },
      /** TRF's own posts on the feed — live at once, signed "TRF". */
      listAnnouncements: async (): Promise<ApiResponse<CommunityPost[]>> => {
        const response = await apiClient.get<ApiResponse<CommunityPost[]>>('/trfer/community/announcements');
        return response.data;
      },
      createAnnouncement: async (data: { body: string; title?: string | null }): Promise<ApiResponse<CommunityPost>> => {
        const response = await apiClient.post<ApiResponse<CommunityPost>>('/trfer/community/announcements', data);
        return response.data;
      },
    },
  },

  /**
   * Feedback (any-role submit, admin-only read/update)
   */
  feedback: {
    submit: async (payload: {
      message: string;
      type?: FeedbackType;
      context?: Record<string, unknown>;
      screenshot?: string;     // base64 data URL, optional
    }): Promise<ApiResponse<{ id: string }>> => {
      const response = await apiClient.post<ApiResponse<{ id: string }>>('/feedback', payload);
      return response.data;
    },

    get: async (id: string): Promise<ApiResponse<FeedbackItem>> => {
      const response = await apiClient.get<ApiResponse<FeedbackItem>>(`/feedback/${id}`);
      return response.data;
    },

    list: async (filters?: {
      status?: FeedbackStatus;
      type?: FeedbackType;
      user_role?: string;
      limit?: number;
    }): Promise<ApiResponse<FeedbackItem[]>> => {
      const response = await apiClient.get<ApiResponse<FeedbackItem[]>>('/feedback', {
        params: filters,
      });
      return response.data;
    },

    counts: async (): Promise<ApiResponse<FeedbackCounts>> => {
      const response = await apiClient.get<ApiResponse<FeedbackCounts>>('/feedback/counts');
      return response.data;
    },

    update: async (
      id: string,
      updates: { status?: 'new' | 'triaged' | 'resolved'; admin_note?: string }
    ): Promise<ApiResponse<FeedbackItem>> => {
      const response = await apiClient.patch<ApiResponse<FeedbackItem>>(`/feedback/${id}`, updates);
      return response.data;
    },
  },

  /**
   * Polls
   */
  polls: {
    // `meta` is already on the wire (polls.js: `meta: { total, limit, offset }`);
    // typing it lets the list say "Showing 100 of 140 polls" instead of
    // simply ending (types/polls.ts ListMeta — "the view MUST say so").
    list: async (filters?: { status?: PollStatus; limit?: number; offset?: number }) => {
      const r = await apiClient.get<ApiResponse<Poll[]> & { meta?: ListMeta }>('/polls', { params: filters });
      return r.data;
    },
    get: async (id: string) => {
      const r = await apiClient.get<ApiResponse<Poll>>(`/polls/${id}`);
      return r.data;
    },
    create: async (payload: {
      title: string; description?: string;
      questions: Array<{ type: QuestionType; prompt: string;
                         choices?: Array<{ label: string }>;
                         rating_scale?: { min: 1; max: 5 };
                         max_text_length?: number }>;
      anonymous: boolean;
      results_visibility: ResultsVisibility;
      audience: PollAudience;
      end_at?: string;
      status?: 'draft' | 'open';
    }) => {
      const r = await apiClient.post<ApiResponse<{ id: string; poll: Poll }>>('/polls', payload);
      return r.data;
    },
    patch: async (id: string, updates: Partial<Pick<Poll, 'title' | 'description' | 'end_at'>>) => {
      const r = await apiClient.patch<ApiResponse<Poll>>(`/polls/${id}`, updates);
      return r.data;
    },
    publish: async (id: string) => {
      const r = await apiClient.post<ApiResponse<Poll>>(`/polls/${id}/publish`);
      return r.data;
    },
    close: async (id: string) => {
      const r = await apiClient.post<ApiResponse<Poll>>(`/polls/${id}/close`);
      return r.data;
    },
    results: async (id: string) => {
      const r = await apiClient.get<ApiResponse<PollResultsResponse>>(`/polls/${id}/results`);
      return r.data;
    },
    feed: async () => {
      const r = await apiClient.get<ApiResponse<PollFeedItem[]>>('/polls/feed');
      return r.data;
    },
    vote: async (id: string, answers: VoteAnswer[]) => {
      const r = await apiClient.post<ApiResponse<null>>(`/polls/${id}/vote`, { answers });
      return r.data;
    },
    audiencePreview: async (audience: PollAudience) => {
      const r = await apiClient.post<ApiResponse<{ count: number }>>(
        '/polls/audience-preview',
        { audience }
      );
      return r.data;
    },
    // Gated per-family view for the admin Results-tab drawer. Server returns
    // 409 (not 200 with []) for anonymous polls — the caller should not call
    // this when poll.anonymous is true. Each fetch is audit-logged server-
    // side, so don't poll this endpoint.
    attributedResponses: async (id: string) => {
      const r = await apiClient.get<ApiResponse<AttributedResponsesPayload>>(
        `/polls/${id}/attributed-responses`
      );
      return r.data;
    },
    // The staff "Not answered yet" section: families on a named send, the
    // count only on an anonymous one (families: null).
    nonResponders: async (id: string) => {
      const r = await apiClient.get<ApiResponse<NonRespondersPayload>>(`/polls/${id}/non-responders`);
      return r.data;
    },
    // In-app reminder to the non-responders' parents; 409 inside 24 h.
    remind: async (id: string) => {
      const r = await apiClient.post<ApiResponse<RemindResult>>(`/polls/${id}/remind`);
      return r.data;
    },
    // Returns the CSV body + the filename the server suggests so the caller
    // can trigger a download. Uses responseType: 'text' so axios returns a
    // string rather than trying to JSON-parse the body.
    exportCsv: async (id: string): Promise<{ csv: string; filename: string }> => {
      const r = await apiClient.get<string>(`/polls/${id}/results.csv`, {
        responseType: 'text',
        transformResponse: [(data) => data],
      });
      const disposition = r.headers['content-disposition'] ?? '';
      const match = disposition.match(/filename="([^"]+)"/);
      return {
        csv: r.data,
        filename: match ? match[1] : `poll-${id}.csv`,
      };
    },
  },

  /**
   * TRFer directory (reassign-target picker)
   */
  trfer: {
    listAvailable: async (): Promise<ApiResponse<AvailableTRFer[]>> => {
      const response = await apiClient.get<ApiResponse<AvailableTRFer[]>>('/trfer/available');
      return response.data;
    },
    // F-023 admin calendar: upcoming patient birthdays + remembrance dates,
    // caseload-scoped for TRFers, program-wide for admins.
    calendar: async (params?: { days?: number }): Promise<ApiResponse<TrferCalendarEvent[]>> => {
      const response = await apiClient.get<ApiResponse<TrferCalendarEvent[]>>('/trfer/calendar', { params });
      return response.data;
    },
    // The sidebar's unread counts (ui-ux-guideline §2.7, 2026-09-17): three
    // numbers plus the ids behind them. The shell polls it; each list marks
    // its own rows from `ids`. `markRead` is the "I opened this" upsert —
    // the thread page, the escalation dialog and the conversation review
    // call it on open.
    unread: async (): Promise<ApiResponse<TrferUnreadCounts>> => {
      const response = await apiClient.get<ApiResponse<TrferUnreadCounts>>('/trfer/unread');
      return response.data;
    },
    markRead: async (kind: TrferUnreadKind, itemId: string): Promise<void> => {
      await apiClient.post('/trfer/unread/read', { kind, item_id: itemId });
    },
  },

  /**
   * Handoff (in-thread TRF handoff)
   */
  handoffs: {
    request: async (body: { conversation_id: string; origin: HandoffOrigin }): Promise<ApiResponse<HandoffRequest>> => {
      const response = await apiClient.post<ApiResponse<HandoffRequest>>('/handoffs/request', body);
      return response.data;
    },
    inbox: async (params?: { scope?: 'mine' | 'all'; country?: string; cohort_year?: string; cycle?: string }): Promise<ApiResponse<HandoffRequest[]>> => {
      const response = await apiClient.get<ApiResponse<HandoffRequest[]>>('/handoffs/inbox', { params });
      return response.data;
    },
    history: async (
      id: string,
      params?: { limit?: number },
    ): Promise<ApiResponse<{ conversation_id: string; messages: unknown[] }>> => {
      const response = await apiClient.get<ApiResponse<{ conversation_id: string; messages: unknown[] }>>(
        `/handoffs/${id}/history`,
        { params },
      );
      return response.data;
    },
    claim: async (id: string): Promise<ApiResponse<HandoffRequest>> => {
      const response = await apiClient.post<ApiResponse<HandoffRequest>>(`/handoffs/${id}/claim`);
      return response.data;
    },
    handback: async (id: string): Promise<ApiResponse<HandoffRequest>> => {
      const response = await apiClient.post<ApiResponse<HandoffRequest>>(`/handoffs/${id}/handback`);
      return response.data;
    },
    reassign: async (id: string, to_trfer_id: string): Promise<ApiResponse<HandoffRequest>> => {
      const response = await apiClient.post<ApiResponse<HandoffRequest>>(`/handoffs/${id}/reassign`, { to_trfer_id });
      return response.data;
    },
    respondNudge: async (id: string, accept: boolean): Promise<ApiResponse<HandoffRequest>> => {
      const response = await apiClient.post<ApiResponse<HandoffRequest>>(`/handoffs/${id}/respond-nudge`, { accept });
      return response.data;
    },
    familyPending: async (): Promise<FamilyPendingResponse> => {
      const response = await apiClient.get<FamilyPendingResponse>('/handoffs/family-pending');
      return response.data;
    },
    message: async (id: string, content: string): Promise<ApiResponse<unknown>> => {
      const response = await apiClient.post<ApiResponse<unknown>>(`/handoffs/${id}/message`, { content });
      return response.data;
    },
    retranslateMessage: async (id: string, messageId: string): Promise<void> => {
      // Called from the "Retry" button in TranslatedMessageBubble when an
      // earlier translation failed. SSE `message_updated` event refreshes the
      // bubble once the retranslation completes — caller does not need the
      // response body.
      await apiClient.post(`/handoffs/${id}/messages/${messageId}/retranslate`);
    },
    heartbeat: async (id: string): Promise<void> => {
      await apiClient.post(`/handoffs/${id}/heartbeat`);
    },
    streamUrl: (id: string): string => `${apiClient.defaults.baseURL}/handoffs/${id}/stream`,
  },

  /**
   * Handoff configuration (read-only access for admin + TRFer leads).
   * Distinct from `api.handoffs` (per-handoff inbox/claim/message) — this
   * surface exposes the platform-wide handoff config that admin owns via
   * /admin/config and that TRFer leads can view but not edit.
   *
   * SHAPE WARNING (B-486): the endpoint is open to every authenticated TRFer,
   * so the backend answers with a NARROW projection of PlatformConfig — only
   * `handoff` (the six trfer_* fields) and
   * `lead_permissions.can_edit_handoff_config`. Every other PlatformConfig
   * key (`features`, `llm`, `agents`, `notifications`, `translation`, the
   * other two lead permissions) is `undefined` here and is only ever
   * populated by the admin-gated /admin/config. The declared
   * `PlatformConfig` return type is the admin superset; read it as
   * best-effort, and gate any new field read on the admin path.
   */
  handoff: {
    getConfig: async (): Promise<ApiResponse<PlatformConfig>> => {
      const response = await apiClient.get<ApiResponse<PlatformConfig>>('/handoff/config');
      return response.data;
    },
    updateConfig: async (updates: Record<string, unknown>): Promise<ApiResponse<PlatformConfig>> => {
      const response = await apiClient.patch<ApiResponse<PlatformConfig>>('/handoff/config', updates);
      return response.data;
    },
  },

  /**
   * Admin-only platform configuration surface. Pairs with the
   * admin-gated /admin/config endpoint used by useAdminConfig /
   * useUpdateConfig. Exposed as a flat method so pages can route saves
   * directly without going through the hooks layer — useful when a
   * single page conditionally writes to either /admin/config (admin) or
   * /handoff/config (lead).
   */
  admin: {
    updateConfig: async (updates: Record<string, unknown>): Promise<ApiResponse<PlatformConfig>> => {
      const response = await apiClient.patch<ApiResponse<PlatformConfig>>('/admin/config', updates);
      return response.data;
    },
    legal: {
      publish: async (
        doc: string,
        payload: {
          version: string;
          title?: string;
          body_markdown: string;
          is_material_change: boolean;
          effective_date?: string;
          confirm?: boolean;
        },
      ): Promise<ApiResponse<{ dry_run: boolean; would_block?: boolean; affected_count?: number; affected_sample?: string[]; version?: string; blocked_count?: number }>> => {
        const r = await apiClient.post(`/admin/legal/${doc}/publish`, payload);
        return r.data;
      },
      unblock: async (body: { user_id?: string; all?: boolean }): Promise<ApiResponse> => {
        const r = await apiClient.post('/admin/legal/unblock', body);
        return r.data;
      },
    },
    /**
     * GET /api/health/detailed — admin-only (health.js:335). Returns the
     * process facts and a live probe of every dependency, including a gRPC
     * healthCheckAll() across the agents. Distinct from /admin/stats, which
     * is a cached aggregate.
     */
    systemHealth: async (): Promise<SystemHealth> => {
      const response = await apiClient.get<SystemHealth>('/health/detailed');
      return response.data;
    },
    /**
     * Per-family community/resource search history (plan
     * 2026-05-30-search-history-tracking). Cursor pagination via `before`
     * (ISO8601); meta.next_before is the cursor for the next page (null at end).
     */
    searchHistory: async (
      familyId: string,
      params?: { limit?: number; before?: string },
    ): Promise<ApiResponse<AdminSearchEvent[]> & { meta: { count: number; next_before: string | null } }> => {
      const r = await apiClient.get(`/admin/families/${familyId}/search-history`, { params });
      return r.data;
    },

    /**
     * Browser-error reports (#1654) — the staff read side of the one
     * client→server error channel (writer: lib/reportClientError.ts). Behind
     * `can_review_applications`: every report today comes from /apply. Contract:
     * docs/architecture/client-error-channel.md.
     */
    clientErrors: {
      /** Last 7 days grouped by token; `meta.since` is the window start. */
      summary: async (): Promise<ApiResponse<ClientErrorSummaryRow[]>> => {
        const response = await apiClient.get<ApiResponse<ClientErrorSummaryRow[]>>(
          '/admin/client-errors/summary',
        );
        return response.data;
      },
    },
  },

  /**
   * Documents (receipts, EOBs, prescription uploads)
   */
  documents: {
    /** #2378: answers with the stored document and, for a `receipt`, the claim it created. */
    upload: async (form: FormData): Promise<ApiResponse<DocumentUploadResult>> => {
      const response = await apiClient.post<ApiResponse<DocumentUploadResult>>(
        '/documents/upload',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },

    fileUrl: (id: string): string => `${apiClient.defaults.baseURL}/documents/${id}/file`,
  },

  /**
   * Reimbursements
   */
  reimbursements: {
    /** The family's ledger: every claim with its receipt's filename/type joined (spec 2026-09-10 §8). */
    listMine: async (): Promise<ApiResponse<ReimbursementClaim[]>> => {
      const response = await apiClient.get<ApiResponse<ReimbursementClaim[]>>('/reimbursements');
      return response.data;
    },

    /** A hand-entered expense: starts as a draft with no receipt (collector spec §4.4). */
    create: async (data: ClaimFamilyEdits): Promise<ApiResponse<ReimbursementClaim>> => {
      const response = await apiClient.post<ApiResponse<ReimbursementClaim>>('/reimbursements', data);
      return response.data;
    },

    /** Edit the family's own fields while the record is `draft` or `needs_info`; submits when complete. */
    update: async (id: string, data: ClaimFamilyEdits): Promise<ApiResponse<ReimbursementClaim>> => {
      const response = await apiClient.patch<ApiResponse<ReimbursementClaim>>(`/reimbursements/${id}`, data);
      return response.data;
    },

    /** Attach (or replace) the receipt on a `draft` / `needs_info` record; runs the scan. */
    attachReceipt: async (id: string, form: FormData): Promise<ApiResponse<ReimbursementClaim>> => {
      const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
        `/reimbursements/${id}/receipt`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },

    /** Only a draft can be deleted — the server refuses anything TRF has seen. */
    remove: async (id: string): Promise<ApiResponse<{ deleted: boolean }>> => {
      const response = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(`/reimbursements/${id}`);
      return response.data;
    },

    get: async (id: string): Promise<ApiResponse<unknown>> => {
      const response = await apiClient.get<ApiResponse<unknown>>(`/reimbursements/${id}`);
      return response.data;
    },

    /**
     * The staff queue. `grants_by_family` (2026-09-14) is the family's approved
     * grants, keyed by family id — the options the row's grant picker offers,
     * the same shape `spend.queue` returns.
     */
    queue: async (
      opts: { status?: string[] } = {},
    ): Promise<ApiResponse<QueueClaim[]> & { grants_by_family?: ClaimQueueGrantsByFamily }> => {
      // `?status=paid,rejected` — the page's Status filter reaches the server
      // (2026-09-17, Q7); absent, the server returns the open work.
      const qs = opts.status && opts.status.length > 0 ? `?status=${encodeURIComponent(opts.status.join(','))}` : '';
      const response = await apiClient.get<ApiResponse<QueueClaim[]> & { grants_by_family?: ClaimQueueGrantsByFamily }>(`/reimbursements/queue${qs}`);
      return response.data;
    },

    /**
     * #3104: the receipt behind a queued claim, for staff. Authed blob fetch —
     * the route is staff-gated, so a plain <a href> won't carry the token.
     * The server serves only the claim's own source document.
     */
    receiptFile: async (id: string): Promise<Blob> => {
      const response = await apiClient.get<Blob>(`/reimbursements/${id}/receipt/file`, { responseType: 'blob' });
      return response.data;
    },

    /**
     * `grant_id` names the grant an APPROVED claim draws from (2026-09-14). The
     * server refuses an approve without it while the family has an approved
     * grant to choose from; reject / needs_info ignore it.
     *
     * `reason` (#2403, D-2403 option A) is the family-visible reason for a
     * reject, stored as the claim's `decision_reason`. Sent only when given;
     * `notes` stays the staff-only review note.
     */
    review: async (
      id: string,
      action: 'approve' | 'reject' | 'needs_info',
      opts: { notes?: string; grant_id?: string; reason?: string } = {},
    ): Promise<ApiResponse<unknown>> => {
      const body: { action: string; notes?: string; grant_id?: string; reason?: string } = { action };
      if (opts.notes !== undefined) body.notes = opts.notes;
      if (opts.grant_id) body.grant_id = opts.grant_id;
      if (opts.reason !== undefined) body.reason = opts.reason;
      const response = await apiClient.patch<ApiResponse<unknown>>(`/reimbursements/${id}/review`, body);
      return response.data;
    },

    /**
     * `ach | check | manual` RECORD a payout a human already made. `billcom`
     * is different in kind: it REQUESTS one from BILL Spend & Expense, so the
     * claim lands in `paying`, not `paid` (#419).
     *
     * A billcom submission can answer 202 `pending_reconciliation` — BILL may
     * or may not have created the reimbursement. That is a 2xx deliberately,
     * and axios resolves it, so callers must branch on the body. It is not a
     * failure, and it must NEVER be retried: an admin reconciles instead.
     */
    /** Ask BILL, on demand, whether any waiting claim has been released. */
    checkBillUpdates: async (): Promise<ApiResponse<BillCheckResult>> => {
      const response = await apiClient.post<ApiResponse<BillCheckResult>>('/reimbursements/check-bill-updates', {});
      return response.data;
    },

    markPaid: async (
      id: string,
      method: PayoutMethod,
    ): Promise<MarkPaidResult> => {
      const response = await apiClient.patch<MarkPaidResult>(`/reimbursements/${id}/mark-paid`, { method });
      return response.data;
    },
  },

  /**
   * TRF ↔ BILL identity mappings (#419, #445). Admin-only.
   *
   * `link` / `unlink` are the manual mapping surface — the platform never
   * auto-provisions a BILL budget. `invite` is the single provisioning call:
   * it creates a BILL *person* so BILL can email them its identity/bank-link
   * flow. `confirm` / `decline` are a TRFer asserting the outcome of that
   * flow, because BILL publishes nothing we could observe instead.
   */
  billIdentities: {
    /**
     * Parents invited into BILL who have not yet been confirmed or declined,
     * oldest wait first. BILL gives us no completion signal by design, so this
     * queue is the only thing telling a TRFer there is a confirmation to make.
     */
        /**
     * Claims stranded in `paying` — submitted, but BILL never confirmed, so no
     * uuid exists. Admin-only: recovering one means deciding whether BILL is
     * holding a reimbursement, which is the decision that can cause a double
     * payment if it is got wrong.
     */
    listStranded: async (): Promise<ApiResponse<unknown[]>> => {
      const response = await apiClient.get<ApiResponse<unknown[]>>('/admin/bill-identities/stranded');
      return response.data;
    },

    /** Scan BILL for our idempotency key. 'recovered' | 'unresolved'. */
    reconcileClaim: async (claimId: string): Promise<ApiResponse<{ outcome: string }>> => {
      const response = await apiClient.post<ApiResponse<{ outcome: string }>>(`/admin/bill-identities/reconcile/${claimId}`, {});
      return response.data;
    },

    /**
     * Release a stranded claim for resubmission. The ONLY call here that can
     * cause a duplicate disbursement, so the confirmation is explicit and
     * undefaulted — a human must have looked in BILL and seen nothing.
     */
    releaseClaim: async (
      claimId: string,
      confirmedAbsentInBill: boolean,
    ): Promise<ApiResponse<{ outcome: string }>> => {
      // The flag is a HUMAN ATTESTATION — "I opened BILL and saw no
      // reimbursement for this claim" — and it is the only thing standing
      // between a release and a duplicate ACH payment. It must be passed in by
      // a caller that actually asked someone. Hardcoding `true` here (as this
      // did) turned a deliberate backend gate into a client-side constant that
      // one unconfirmed click satisfied.
      const response = await apiClient.post<ApiResponse<{ outcome: string }>>(
        `/admin/bill-identities/reconcile/${claimId}/release`,
        { confirmed_absent_in_bill: confirmedAbsentInBill },
      );
      return response.data;
    },

    listPending: async (env: BillEnv = 'sandbox'): Promise<ApiResponse<BillIdentityRow[]>> => {
      const response = await apiClient.get<ApiResponse<BillIdentityRow[]>>('/admin/bill-identities', {
        params: { env, pending: 1 },
      });
      return response.data;
    },
    list: async (env: BillEnv = 'sandbox'): Promise<ApiResponse<BillIdentityRow[]>> => {
      const response = await apiClient.get<ApiResponse<BillIdentityRow[]>>('/admin/bill-identities', {
        params: { env },
      });
      return response.data;
    },

    link: async (payload: BillIdentityLinkPayload): Promise<ApiResponse<BillIdentityRow>> => {
      const response = await apiClient.post<ApiResponse<BillIdentityRow>>('/admin/bill-identities', payload);
      return response.data;
    },

    unlink: async (id: string): Promise<ApiResponse<BillIdentityRow>> => {
      const response = await apiClient.delete<ApiResponse<BillIdentityRow>>(`/admin/bill-identities/${id}`);
      return response.data;
    },

    invite: async (trfUserId: string): Promise<ApiResponse<BillOnboardingResult>> => {
      const response = await apiClient.post<ApiResponse<BillOnboardingResult>>('/admin/bill-identities/invite', {
        trf_user_id: trfUserId,
      });
      return response.data;
    },

    confirm: async (id: string): Promise<ApiResponse<BillOnboardingResult>> => {
      const response = await apiClient.post<ApiResponse<BillOnboardingResult>>(`/admin/bill-identities/${id}/confirm`, {});
      return response.data;
    },

    decline: async (id: string): Promise<ApiResponse<BillOnboardingResult>> => {
      const response = await apiClient.post<ApiResponse<BillOnboardingResult>>(`/admin/bill-identities/${id}/decline`, {});
      return response.data;
    },

    /**
     * Recovery for a claim stranded in `paying` by a submission BILL never
     * confirmed. Explicitly human-triggered: it goes and LOOKS in BILL rather
     * than resubmitting, which is the difference between recovering a payout
     * and making a second one.
     */
    reconcile: async (claimId: string): Promise<ApiResponse<{ outcome: 'recovered' | 'released' }>> => {
      const response = await apiClient.post<ApiResponse<{ outcome: 'recovered' | 'released' }>>(
        `/admin/bill-identities/reconcile/${claimId}`, {},
      );
      return response.data;
    },
  },

  /**
   * Translation reports — "Report bad translation" submissions.
   *
   * The create endpoint is open to any authenticated user (called from
   * ViewOriginalModal via raw fetch, so not exercised through this client).
   * The list + markReviewed endpoints are admin-only.
   */
  translationReports: {
    list: async (opts?: { limit?: number }): Promise<ApiResponse<TranslationReport[]>> => {
      const response = await apiClient.get<ApiResponse<TranslationReport[]>>('/translation-reports', {
        params: opts,
      });
      return response.data;
    },

    markReviewed: async (id: string): Promise<void> => {
      await apiClient.post(`/translation-reports/${id}/reviewed`);
    },
  },

  /**
   * Grant requests (plan 2026-05-30-grant-request-domain).
   * adminList / create / get / transition are admin + Lead-TRFer (gated on
   * can_assign_trfers). `mine` is the family-scoped read for the parent surface.
   */
  grants: {
    /**
     * `status` takes one status or a comma list (`submitted,under_review`,
     * 2026-09-19). `meta.signature_payments_due` counts the Signature awards
     * with a payment due this month or earlier — the Home lane reads it here.
     */
    adminList: async (params?: { family_id?: string; type?: string; status?: string; limit?: number }): Promise<ApiResponse<GrantRequest[]>> => {
      const response = await apiClient.get<ApiResponse<GrantRequest[]>>('/admin/grants', { params });
      return response.data;
    },
    create: async (data: GrantCreateInput): Promise<ApiResponse<GrantRequest>> => {
      const response = await apiClient.post<ApiResponse<GrantRequest>>('/admin/grants', data);
      return response.data;
    },
    get: async (id: string): Promise<ApiResponse<GrantRequest>> => {
      const response = await apiClient.get<ApiResponse<GrantRequest>>(`/admin/grants/${id}`);
      return response.data;
    },
    /** #2525 / #2524: the Decide dialog's context — running Signature schedules and country. */
    decisionContext: async (id: string): Promise<ApiResponse<GrantDecisionContext>> => {
      const response = await apiClient.get<ApiResponse<GrantDecisionContext>>(`/admin/grants/${id}/decision-context`);
      return response.data;
    },
    transition: async (id: string, body: GrantTransitionInput): Promise<ApiResponse<GrantRequest>> => {
      const response = await apiClient.patch<ApiResponse<GrantRequest>>(`/admin/grants/${id}`, body);
      return response.data;
    },
    mine: async (params?: { status?: string }): Promise<ApiResponse<GrantRequest[]>> => {
      const response = await apiClient.get<ApiResponse<GrantRequest[]>>('/grants', { params });
      return response.data;
    },
    linkReceipt: async (grantId: string, claimId: string): Promise<ApiResponse<ReimbursementClaimLite>> => {
      const response = await apiClient.post<ApiResponse<ReimbursementClaimLite>>(`/grants/${grantId}/link`, { claim_id: claimId });
      return response.data;
    },
    unlinkReceipt: async (grantId: string, claimId: string): Promise<ApiResponse<ReimbursementClaimLite>> => {
      const response = await apiClient.post<ApiResponse<ReimbursementClaimLite>>(`/grants/${grantId}/unlink`, { claim_id: claimId });
      return response.data;
    },

    // ── Grant awards wave 2026-09-19 (contracts C-1, C-3) ──

    /** C-1: the configured award amounts per country and type. Any staff. */
    amounts: async (): Promise<ApiResponse<GrantAmountsResponse>> => {
      const response = await apiClient.get<ApiResponse<GrantAmountsResponse>>('/trfer/grants/amounts');
      return response.data;
    },
    /** C-1: replace the amounts. `can_assign_trfers` only; future awards only. */
    setAmounts: async (amounts: GrantAmounts): Promise<ApiResponse<GrantAmountsResponse>> => {
      const response = await apiClient.put<ApiResponse<GrantAmountsResponse>>('/admin/grants/amounts', { amounts });
      return response.data;
    },
    /** C-3: one row per award (approved | fulfilled requests). Any staff. */
    awards: async (params: AwardsQuery = {}): Promise<ApiResponse<AwardsResponse>> => {
      const response = await apiClient.get<ApiResponse<AwardsResponse>>('/trfer/grants/awards', {
        params: cleanParams(params),
      });
      return response.data;
    },
    /**
     * C-3: the same filters as a CSV. The route is staff-gated, so a plain
     * `<a href>` would not carry the token — an authed blob download, the
     * pattern every staff CSV on the platform uses.
     */
    exportAwardsCsv: async (params: AwardsQuery = {}): Promise<void> => {
      const res = await apiClient.get<Blob>('/trfer/grants/awards.csv', {
        responseType: 'blob',
        params: cleanParams(params),
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `awards-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    /** C-3: record a payment made against an approved award (409 when not approved). */
    recordPayment: async (id: string, body: AwardPaymentInput): Promise<ApiResponse<AwardPaymentResult>> => {
      const response = await apiClient.post<ApiResponse<AwardPaymentResult>>(`/admin/grants/${id}/payments`, body);
      return response.data;
    },
    /** C-3: stop a scheduled award's payments falling due. */
    pause: async (id: string): Promise<ApiResponse<GrantRequest>> => {
      const response = await apiClient.post<ApiResponse<GrantRequest>>(`/admin/grants/${id}/pause`);
      return response.data;
    },
    resume: async (id: string): Promise<ApiResponse<GrantRequest>> => {
      const response = await apiClient.post<ApiResponse<GrantRequest>>(`/admin/grants/${id}/resume`);
      return response.data;
    },
  },

  /**
   * Family applications (applicant-facing). Plan: family-application.
   */
  applications: {
    getMine: async (): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.get<ApiResponse<FamilyApplication>>('/applications/me');
      return response.data;
    },
    // B6 (F-013): when ApplyLandingPage stashed a ?invite= token, pass it
    // through on draft creation (the backend validates/redeems it whenever
    // present, and requires one of invite/code when
    // ConfigSettings.application_codes_mode is `required` — #1018) and clear
    // it once the draft exists. With no stashed token the request is
    // byte-identical to the pre-B6 behavior.
    // GH-415: the same implicit threading now also carries a stashed
    // application code. If BOTH are stashed we send both and let the backend
    // arbitrate — it prefers invite_token and leaves the code unconsumed.
    // #1018 Task 14 (Part 1): a stashed referral "slow path" value threads
    // through the same way, onto the three wire names the backend tests pin
    // exactly (`referral_hospital_id` / `referral_hospital_other` /
    // `referral_contact_name`) — included only when present, since every
    // field in `ReferralBlock` is optional and an empty string is not an
    // answer. Clearing it after success matters as much as sending it: a
    // stale referral leaking into a family's NEXT application (a different
    // child, a different hospital) would be a data-integrity bug, not just
    // stale UI state.
    // #1012/#1014: the stashed pre-screen answers ride along on the same
    // implicit thread. The server re-evaluates them against the published
    // definition and refuses on a miss — this call is the one the eligibility
    // gate actually guards, which is why a family who never answered gets a
    // 422 rather than a draft.
    createDraft: async (): Promise<ApiResponse<FamilyApplication>> => {
      const inviteToken = readApplyInviteToken();
      const applicationCode = readApplyCode();
      const referral = readApplyReferral();
      const screeningAnswers = readApplyScreening();
      const payload: {
        invite_token?: string;
        application_code?: string;
        referral_hospital_id?: string;
        referral_hospital_other?: string;
        referral_contact_name?: string;
        screening_answers?: ScreeningAnswers;
      } = {};
      if (inviteToken) payload.invite_token = inviteToken;
      if (applicationCode) payload.application_code = applicationCode;
      if (referral?.hospital_id) payload.referral_hospital_id = referral.hospital_id;
      if (referral?.hospital_other?.trim()) payload.referral_hospital_other = referral.hospital_other.trim();
      if (referral?.contact_name?.trim()) payload.referral_contact_name = referral.contact_name.trim();
      if (screeningAnswers) payload.screening_answers = screeningAnswers;
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        '/applications',
        Object.keys(payload).length > 0 ? payload : undefined,
      );
      clearApplyInviteToken();
      clearApplyCode();
      clearApplyReferral();
      // Only cleared on success: a refused create leaves the answers in place
      // so a family who fixes something is not asked to retype all seven.
      clearApplyScreening();
      return response.data;
    },
    /**
     * #2236 — the family withdraws its own application, from any stage before
     * admission. No reason is asked. Silent server-side: no email, no DocuSign.
     */
    withdraw: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(`/applications/${id}/withdraw`);
      return response.data;
    },
    // B6b: PUBLIC pre-signup check of a ?invite= token. Returns only
    // { valid } — unknown/expired/revoked/redeemed all read as valid:false.
    // #2951: `renewal:true` rides beside valid:true for a link minted "for
    // renewal families"; /apply then skips the pre-screen (2026-10-28).
    inviteStatus: async (token: string): Promise<ApiResponse<{ valid: boolean; renewal?: boolean }>> => {
      const response = await apiClient.get<ApiResponse<{ valid: boolean; renewal?: boolean }>>(
        '/applications/invite/status',
        { params: { token } },
      );
      return response.data;
    },
    // GH-415: PUBLIC pre-signup check of a ?code= / typed referral code.
    // `valid:false` on its own is the generic collapse (unknown / revoked).
    // `typo:true` means the check symbol is wrong (computed, no database
    // access). `renewal:true` rides beside valid:true for a code with Renewal
    // Families on; /apply then skips the pre-screen (2026-10-28). The check
    // never counts a use.
    codeStatus: async (
      code: string,
    ): Promise<ApiResponse<{ valid: boolean; typo?: boolean; renewal?: boolean }>> => {
      const response = await apiClient.get<ApiResponse<{ valid: boolean; typo?: boolean; renewal?: boolean }>>(
        '/applications/code/status',
        { params: { code } },
      );
      return response.data;
    },
    // #1017 architecture-review addendum, C2b: PUBLIC read of the runtime,
    // staff-settable `application_codes_mode` (ConfigSettings is staff-only,
    // so this is the only way ApplyLandingPage can learn it). Replaces the
    // retired frontend AUTH_POLICY.inviteRequired mirror — see
    // lib/auth-policy.ts. Answers ONLY `{ mode }`.
    gate: async (): Promise<ApiResponse<{ mode: 'required' | 'optional' | 'disabled'; canada_intake_closed?: boolean; accepting_submissions?: boolean; closed_notice?: string | null; new_families_open_on?: string }>> => { // #2199, L3-c, #3452
      const response = await apiClient.get<ApiResponse<{ mode: 'required' | 'optional' | 'disabled' }>>(
        '/applications/gate',
      );
      return response.data;
    },
    // #2197: which document slots a family must fill before submitting — one
    // platform-wide list (initial and renewal), staff-set on Settings →
    // Applications and enforced by the server at both submit routes. The
    // wizard and the staff tab read it here so the chips, the client block and
    // the server gate cannot disagree. Authenticated; answers only the list.
    documentRequirements: async (): Promise<ApiResponse<{ required: string[] }>> => {
      const response = await apiClient.get<ApiResponse<{ required: string[] }>>(
        '/applications/document-requirements',
      );
      return response.data;
    },
    saveSection: async (
      id: string,
      section: string,
      values: Record<string, unknown>,
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.patch<ApiResponse<FamilyApplication>>(
        `/applications/${id}/section`,
        { section, values },
      );
      return response.data;
    },
    /** #2756: the unload twin of `saveSection` (see patchSectionKeepalive). */
    saveSectionOnUnload: (
      id: string,
      section: string,
      values: Record<string, unknown>,
    ): Promise<ApiResponse<FamilyApplication>> => patchSectionKeepalive(`/applications/${id}/section`, section, values),
    /**
     * #2440: record the step the family is ON (`[a-z0-9_.-]{1,64}`), so a
     * sign-out, refresh or Back reopens there. The B-403 clamp still wins.
     */
    saveResumeStep: async (id: string, step: string): Promise<ApiResponse<ResumeStepResult>> => {
      const response = await apiClient.patch<ApiResponse<ResumeStepResult>>(
        `/applications/${id}/resume-step`,
        { resume_step: step },
      );
      return response.data;
    },
    /**
     * `version_n` is the published version the wizard RENDERED (#1095 item 1).
     * The wizard pins its definition at mount, so a republish mid-application
     * would otherwise have the server validate and stamp a version the family
     * never saw. Optional on the wire: a client that omits it is served the
     * live version, exactly as before this shipped.
     */
    submit: async (
      id: string,
      payload?: { version_n: number },
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(`/applications/${id}/submit`, payload);
      return response.data;
    },
    uploadDocument: async (
      id: string,
      formData: FormData,
      onProgress?: (percent: number) => void,
    ): Promise<ApiResponse<ApplicationDocumentMeta>> => {
      const response = await apiClient.post<ApiResponse<ApplicationDocumentMeta>>(
        `/applications/${id}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // #2477: per-file progress for the documents roster.
          onUploadProgress: onProgress
            ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
            : undefined,
        },
      );
      return response.data;
    },
    listDocuments: async (id: string): Promise<ApiResponse<ApplicationDocumentMeta[]>> => {
      const response = await apiClient.get<ApiResponse<ApplicationDocumentMeta[]>>(
        `/applications/${id}/documents`,
      );
      return response.data;
    },
    /** #2471: the bytes of one of the family's own documents, for preview. */
    documentFile: async (id: string, docId: string): Promise<Blob> => {
      const response = await apiClient.get<Blob>(`/applications/${id}/documents/${docId}/file`, { responseType: 'blob' });
      return response.data;
    },
    /** #2471: remove one of the family's own uploads (draft only). */
    removeDocument: async (id: string, docId: string): Promise<ApiResponse<{ id: string; kind: string }>> => {
      const response = await apiClient.delete<ApiResponse<{ id: string; kind: string }>>(`/applications/${id}/documents/${docId}`);
      return response.data;
    },
    /** #2471: Undo of `removeDocument`. */
    restoreDocument: async (id: string, docId: string): Promise<ApiResponse<{ id: string; kind: string }>> => {
      const response = await apiClient.post<ApiResponse<{ id: string; kind: string }>>(`/applications/${id}/documents/${docId}/restore`);
      return response.data;
    },
    /**
     * D-FB-12: upload the answer to a builder-minted `file` question.
     *
     * Addressed by FIELD id, not by a code-pinned document kind — and the
     * server writes the answer itself, so the response carries the reference
     * rather than the caller inventing one.
     */
    uploadFieldDocument: async (
      id: string,
      fieldId: string,
      formData: FormData,
    ): Promise<ApiResponse<{ id: string; field_id: string; filename: string }>> => {
      const response = await apiClient.post<ApiResponse<{ id: string; field_id: string; filename: string }>>(
        `/applications/${id}/fields/${encodeURIComponent(fieldId)}/document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
  },

  /**
   * Partner hospitals (#1018) — PUBLIC typeahead behind the /apply referral
   * "slow path" (`ReferralBlock`). Same pre-auth slot as
   * applications.codeStatus / applications.gate: no `authenticate`, rate
   * limited server-side, `Cache-Control: no-store`. A blank query is the
   * caller's job to avoid — the route treats it as "no results" rather than
   * erroring, so an empty string is a wasted round trip, not a bug.
   */
  partnerHospitals: {
    search: async (q: string): Promise<ApiResponse<PartnerHospitalSummary[]>> => {
      const response = await apiClient.get<ApiResponse<PartnerHospitalSummary[]>>(
        '/partner-hospitals',
        { params: { q } },
      );
      return response.data;
    },
    /**
     * Staff write side (#1151): `/api/trfer/partner-hospitals`, TRFer or
     * admin. Moved off the admin mount 2026-09-20 — the partner list is
     * intake reference data every TRFer maintains. Named `staff`, not
     * `admin` — the endpoint has not been admin-only since that move, and a
     * key that still said `admin` was stale naming on a no-longer-admin
     * surface (final whole-branch review MINOR 8, 2026-09-21).
     */
    staff: {
      list: async (): Promise<ApiResponse<PartnerHospitalAdminRow[]>> => {
        const response = await apiClient.get<ApiResponse<PartnerHospitalAdminRow[]>>('/trfer/partner-hospitals');
        return response.data;
      },
      create: async (payload: { name: string; city?: string | null; state?: string | null }): Promise<ApiResponse<PartnerHospitalAdminRow>> => {
        const response = await apiClient.post<ApiResponse<PartnerHospitalAdminRow>>('/trfer/partner-hospitals', payload);
        return response.data;
      },
      update: async (id: string, payload: PartnerHospitalEdit): Promise<ApiResponse<PartnerHospitalAdminRow>> => {
        const response = await apiClient.patch<ApiResponse<PartnerHospitalAdminRow>>(`/trfer/partner-hospitals/${id}`, payload);
        return response.data;
      },
      setActive: async (id: string, active: boolean): Promise<ApiResponse<PartnerHospitalAdminRow>> => {
        const response = await apiClient.patch<ApiResponse<PartnerHospitalAdminRow>>(`/trfer/partner-hospitals/${id}/active`, { active });
        return response.data;
      },
    },
  },

  /**
   * Mailing-address validation proxy (#1059). Dark by default: when the
   * backend runs ADDRESS_VALIDATION_MODE=off the answer is { mode: 'off' } and
   * callers stay silent. The Google key lives server-side only — this
   * namespace never sees it.
   */
  addressValidation: {
    validate: async (
      address: { street: string; apt?: string; city?: string; state?: string; zip?: string; country?: string },
    ): Promise<ApiResponse<AddressValidationData>> => {
      const response = await apiClient.post<ApiResponse<AddressValidationData>>('/address-validation', { address });
      return response.data;
    },
  },

  /**
   * Published form definitions (form-builder wave PR-4).
   *
   * An ADDITIVE namespace — nothing existing moves. Two routes only, matching
   * the spec's per-kind access table: the pre-screen is public because the
   * eligibility gate runs before an account exists, and the application is
   * behind plain authentication because applicants and parents render it.
   * `checkin`/`custom` definitions are never served to families, so there is
   * deliberately no accessor for them here.
   */
  forms: {
    /**
     * STAFF — every definition with a compact version summary. The builder's
     * templates list (PR-5) renders from this and nothing else; the backend
     * guards it TRFer-or-Admin, both at full capability.
     */
    list: async (): Promise<ApiResponse<FormDefinitionSummary[]>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionSummary[]>>('/forms');
      return response.data;
    },
    /**
     * STAFF — #3109: every definition, ARCHIVED ones included (`?includeArchived=true`).
     * The builder's "Archived forms" restore list reads this; the picker keeps
     * reading `list`, which the server limits to active forms.
     */
    listWithArchived: async (): Promise<ApiResponse<FormDefinitionSummary[]>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionSummary[]>>('/forms', {
        params: { includeArchived: true },
      });
      return response.data;
    },
    /**
     * STAFF — #3109: archive's undo. Restores the definition to `active`;
     * publishes nothing. 409 when the form is not archived.
     */
    unarchive: async (slug: string): Promise<ApiResponse<FormDefinitionDoc>> => {
      const response = await apiClient.post<ApiResponse<FormDefinitionDoc>>(
        `/forms/${encodeURIComponent(slug)}/unarchive`,
      );
      return response.data;
    },
    /** PUBLIC — reachable pre-auth from /apply. `lang` (#2109 R2-B19) asks
     *  for English plus that one translation; without it, English only. */
    publishedPrescreen: async (options?: { lang?: string }): Promise<ApiResponse<FormDefinitionVersion>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionVersion>>('/forms/published/prescreen', {
        params: options?.lang && options.lang !== 'en' ? { lang: options.lang } : undefined,
      });
      return response.data;
    },
    /** Any signed-in user — the wizard and the renewal wrapper share it (D-FB-6). */
    publishedApplication: async (options?: { lang?: string }): Promise<ApiResponse<FormDefinitionVersion>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionVersion>>('/forms/published/application', {
        params: options?.lang && options.lang !== 'en' ? { lang: options.lang } : undefined,
      });
      return response.data;
    },
    /**
     * STAFF — the version an application was submitted against, so a staff
     * surface renders the questions as they were ANSWERED rather than as they
     * read today.
     */
    stampedForApplication: async (id: string): Promise<ApiResponse<FormDefinitionVersion>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionVersion>>(`/forms/stamped/application/${id}`);
      return response.data;
    },
    /** STAFF — the same, resolved through an admitted family's source application. */
    stampedForFamily: async (id: string): Promise<ApiResponse<FormDefinitionVersion>> => {
      const response = await apiClient.get<ApiResponse<FormDefinitionVersion>>(`/forms/stamped/family/${id}`);
      return response.data;
    },
    /**
     * STAFF — one definition plus EVERY version row, draft included. This is
     * simultaneously the builder's load (the draft row is the tracked body's
     * source) and the History panel's data: there is no separate history
     * route, and the versions arrive oldest-first.
     */
    get: async (slug: string): Promise<ApiResponse<FormDetail>> => {
      const response = await apiClient.get<ApiResponse<FormDetail>>(`/forms/${slug}`);
      return response.data;
    },
    /**
     * STAFF — save draft edits. Immutability rules 1/2/3/5 are enforced
     * server-side on this path; `payload.expected_updated_at` is the CAS token
     * (the draft row's `updated_at` as loaded) — a lost last-writer-wins race
     * comes back as HTTP 409, which `formBuilder.isDraftConflict` recognizes.
     * New fields/options are sent WITHOUT id/value; the server mints them.
     */
    saveDraft: async (slug: string, payload: SaveDraftPayload): Promise<ApiResponse<FormVersionDoc>> => {
      const response = await apiClient.patch<ApiResponse<FormVersionDoc>>(`/forms/${slug}/draft`, payload);
      return response.data;
    },
    /**
     * STAFF — throw the open draft away (#1532). Returns the definition as it
     * stands afterwards, with `draft_version_id: null`.
     *
     * The draft ROW is deleted, not marked superseded: `buildBaseline` treats
     * superseded exactly like published when it collects "ids this definition
     * has ever issued", so superseding would reserve every id the draft minted
     * against a future real question of the same name, permanently. Nothing
     * published moves, and no family answer is touched.
     *
     * `expectedUpdatedAt` is REQUIRED — the draft row's `updated_at` as loaded
     * (`BuilderDraftState.expectedUpdatedAt`), the same CAS token `saveDraft`
     * carries. The draft row is shared per DEFINITION, not per person, so this
     * is what stops one staff member throwing away edits another has saved
     * since the page loaded. A lost race comes back as HTTP 409, which
     * `formBuilder.isDraftConflict` recognizes.
     */
    discardDraft: async (slug: string, expectedUpdatedAt: string): Promise<ApiResponse<FormDefinitionDoc>> => {
      const response = await apiClient.delete<ApiResponse<FormDefinitionDoc>>(
        `/forms/${encodeURIComponent(slug)}/draft`,
        { data: { expected_updated_at: expectedUpdatedAt } },
      );
      return response.data;
    },
    /**
     * STAFF — draft → published (prior published → superseded). The change
     * note is REQUIRED: History is the only record of why a form moved. The
     * floor-trio and renderability checks run server-side before anything is
     * written (immutability rule 4 / guardrail c).
     *
     * #3108: `expected_updated_at` is REQUIRED — the draft row's `updated_at`
     * as the publisher reviewed it (`BuilderDraftState.expectedUpdatedAt`), the
     * same CAS token `saveDraft` and `discardDraft` carry. A colleague's save
     * since then comes back as HTTP 409 ("Draft conflict: …"), which
     * `formBuilder.isDraftConflict` recognizes; a missing token is a 400.
     */
    publish: async (
      slug: string,
      payload: { change_note: string; expected_updated_at: string },
    ): Promise<ApiResponse<FormVersionDoc>> => {
      // #2109 (Victor 2026-09-30 R2): publishing first machine-translates the
      // edited words into every language the form carries. A typical edit
      // takes seconds; a whole form in one language about 42 s, so this call
      // allows longer than the client default. `meta.translation` reports
      // what stayed in English.
      const response = await apiClient.post<ApiResponse<FormVersionDoc>>(
        `/forms/${slug}/publish`, payload, { timeout: 120_000 },
      );
      return response.data;
    },
    /** STAFF — #2109 R2: machine-translate ONE language's stale and missing
     *  strings on the open draft so the Languages tab can offer it. Returns the
     *  draft; `meta.translation` is the report and `meta.gate_failures` the
     *  required strings still untranslated. */
    translateDraftLanguage: async (slug: string, code: string, expectedUpdatedAt: string | null): Promise<ApiResponse<FormVersionDoc>> => {
      const response = await apiClient.post<ApiResponse<FormVersionDoc>>(
        `/forms/${slug}/draft/translate`,
        { language: code, ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}) },
        { timeout: 120_000 },
      );
      return response.data;
    },
    /** STAFF — re-publish an old version's body as a NEW version. History is
     *  append-only; nothing is overwritten. */
    revert: async (slug: string, versionN: number): Promise<ApiResponse<FormVersionDoc>> => {
      const response = await apiClient.post<ApiResponse<FormVersionDoc>>(`/forms/${slug}/revert/${versionN}`);
      return response.data;
    },
    /**
     * STAFF — the retire-impact facts for one field (PR-5 §The consequence
     * layer; D-FB-13). Every value names the query that produced it, `unknown`
     * is distinct from `0`, and counts arrive scoped to the caller's own lane
     * for plain TRFers (criterion 6 — the server does the scoping, the modal
     * renders the scope label).
     *
     * ONE SOURCE PER KIND, so `FieldRetireImpact` is a union discriminated on
     * `kind` — read `data.items` only after narrowing:
     *   - `application` — stored family answers.
     *   - `checkin` / `custom` — the sends minted from the definition and the
     *     answers already given to them (#1106). Those counts are HISTORICAL:
     *     retiring affects FUTURE SENDS ONLY (ruling 2026-08-27), because a
     *     sent instance is a frozen copy nothing rewrites. They describe what
     *     happened and stays readable — not what breaks if you proceed.
     *   - `prescreen` — 404, permanently. Those numbers do not exist: the
     *     families a screening question STOPPED never created an account, so
     *     nothing about them was kept, and the route refuses rather than
     *     pretending otherwise. (#1210 — this said "screening answers are never
     *     persisted" until #1014 made that false; answers carried into an
     *     application ARE stored, but describe only the families who passed.)
     */
    fieldImpact: async (slug: string, fieldId: string): Promise<ApiResponse<FieldRetireImpact>> => {
      const response = await apiClient.get<ApiResponse<FieldRetireImpact>>(`/forms/${slug}/impact/field/${fieldId}`);
      return response.data;
    },
    /**
     * STAFF — create a `custom` form, blank or as a copy of `duplicate_of`
     * (D-FB-6: Duplicate is the divergence path; the copy gets its own
     * id-space and can never disturb the original's stored answers).
     * #3109: a copy keeps its source's kind unless `kind: 'custom'` is sent;
     * the server refuses a second application or pre-screen (409).
     */
    duplicate: async (input: {
      slug: string; title: string; duplicate_of?: string;
      kind?: 'prescreen' | 'application' | 'checkin' | 'custom';
    }): Promise<ApiResponse<FormDefinitionDoc>> => {
      const response = await apiClient.post<ApiResponse<FormDefinitionDoc>>('/forms', input);
      return response.data;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PR-6 send pipeline. A self-contained block at the end of this namespace
    // — nothing above it moves, so PR-5's additions merge without a conflict.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Mint a poll instance from the PUBLISHED version of a check-in/custom
     * definition and send it now, or park it as `scheduled`.
     * The route rejects `application`/`prescreen` with a 400 — callers must
     * not offer the control for those kinds (`canSendKind`).
     */
    send: async (slug: string, payload: SendRequest): Promise<ApiResponse<SendInstance>> => {
      const response = await apiClient.post<ApiResponse<SendInstance>>(
        `/forms/${encodeURIComponent(slug)}/send`,
        payload,
      );
      return response.data;
    },

    /**
     * Every send minted from one definition.
     *
     * CONTRACT NOTE: the definition match is the SERVER's — `GET /api/polls`
     * validates `form_definition_id` (`polls.js:310`) and `Poll.list` matches
     * on it, precisely "so the answer cannot be quietly truncated by the limit
     * before the caller filters". Filtering here instead was a live defect:
     * an admin's `/polls` is org-wide, so if the newest 200 polls org-wide
     * held none of this definition's older sends, SendsView rendered
     * "This form has never been sent." as an authoritative empty.
     *
     * The SCOPE is the server's too — non-admins get only polls they created
     * (`polls.js:238`) — and that is the scope the UI must label.
     */
    sendInstances: async (
      definitionId: string,
      opts: { limit?: number; offset?: number } = {},
    ): Promise<SendInstancesResult> => {
      const response = await apiClient.get<SendInstancesResult>('/polls', {
        params: {
          limit: opts.limit ?? 200,
          offset: opts.offset ?? 0,
          form_definition_id: definitionId,
        },
      });
      return {
        status: 'success',
        success: true,
        data: response.data.data ?? [],
        // #1108: pass the truncation facts through. This function used to
        // build its own return object and drop `meta` on the floor, which is
        // exactly why the Sends view could not say it was showing a page.
        // Left undefined when the server sends none — absent is unknown, not
        // zero.
        meta: response.data.meta,
      };
    },

    /**
     * The families this staffer may target. THE SERVER SCOPES THIS — a TRFer
     * gets `/trfer/families` (their caseload, `Family.findByTRFer`, one
     * unpaginated list), an admin gets `/admin/families` (org-wide, PAGED).
     * The client never filters a caseload itself; it only renders the label
     * saying which list it received.
     *
     * The admin path pages to exhaustion rather than taking the first page.
     * It used to send no params at all, so it got `/admin/families`' default
     * `limit=100` (`admin-families.js:84`) and discarded `meta.total` — and
     * the picker then drew the legend "Families (100)" over an org of any
     * size, with family #101 unreachable by name and nothing on screen saying
     * so. `meta.truncated` is what the picker needs to keep its own doctrine
     * honest: *an audience picked from a partial list is not the audience you
     * meant.* Under the page bound truncation is impossible; past it, it is
     * at least declared.
     */
    sendCandidateFamilies: async (
      scope: SendScope,
    ): Promise<SendCandidateFamiliesResult> => {
      interface FamilyRow {
        _id: string;
        intake?: { family_last_name?: string | null };
        primary_caregiver?: { name?: string | null };
        patient?: { first_name?: string | null; hospital?: string | null; treatment_phase?: string | null };
        assigned_trfer_id?: string | null;
      }
      /** `meta` lives only on the success branch of `ApiResponse`, and these
       *  reads happen before any narrowing — so the payload is typed directly. */
      interface FamiliesPayload { data?: FamilyRow[]; meta?: { total?: number } }
      /** `/admin/families` caps `limit` at 500 (`admin-families.js:79`). */
      const PAGE = 500;
      /** A bound so a bad `meta.total` cannot spin the client forever. */
      const MAX_PAGES = 10;

      const toCandidate = (f: FamilyRow): SendCandidateFamily => ({
        _id: String(f._id),
        display: getFamilyDisplayName({
          familyLastName: f.intake?.family_last_name,
          primaryCaregiverName: f.primary_caregiver?.name,
          patientFirstName: f.patient?.first_name,
        }),
        hospital: f.patient?.hospital ?? null,
        treatment_phase: f.patient?.treatment_phase ?? null,
        // Present on the org-wide list (`/admin/families` returns whole family
        // docs); absent from `/trfer/families` rows only in the sense that they
        // are all the caller's anyway. `null` is a REAL value here — a family
        // assigned to nobody — so it is normalized rather than dropped.
        assigned_trfer_id: f.assigned_trfer_id == null ? null : String(f.assigned_trfer_id),
      });

      // A Lead TRFer takes the ADMIN path (#1131). `/admin/families` gates on
      // `requirePermission('can_assign_trfers')` (admin-families.js:66), not on
      // role, so this is the server's own reading of the tier rather than a
      // client-side widening: the same permission `hasOrgWideCaseloadReach`
      // resolves is the one that opens this list.
      if (scope === 'trfer') {
        const response = await apiClient.get<FamiliesPayload>('/trfer/families');
        const data = (response.data.data ?? []).map(toCandidate);
        return {
          status: 'success',
          success: true,
          data,
          meta: { total: data.length, truncated: false },
        };
      }

      const rows: FamilyRow[] = [];
      let total = 0;
      let truncated = false;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const response = await apiClient.get<FamiliesPayload>('/admin/families', {
          params: { limit: PAGE, offset: page * PAGE },
        });
        const batch = response.data.data ?? [];
        rows.push(...batch);
        total = Number(response.data.meta?.total ?? rows.length);
        // A short page means the server has no more to give, whatever `total`
        // claims — trust the page, not the counter.
        if (batch.length < PAGE || rows.length >= total) break;
        if (page === MAX_PAGES - 1) truncated = true;
      }

      return {
        status: 'success',
        success: true,
        data: rows.map(toCandidate),
        meta: { total: Math.max(total, rows.length), truncated },
      };
    },

    /**
     * The TRFer roster a Lead narrows a send by (#1131).
     *
     * `GET /trfer/users?role=trfer` — the user-management list, gated on
     * `requirePermission('can_assign_trfers')` (`trfer.js:1882`), which is the
     * same permission that makes someone a Lead in the first place.
     *
     * NOT `/trfer/available`, tempting as that is: it is the handoff-reassign
     * directory, and it drops the caller themselves (`trfer.js` GET /available).
     * A roster with silent
     * omissions is the wrong foundation for a control whose entire job is
     * saying who a send reaches — a colleague left out
     * would take their whole caseload out of an audience nobody knew was short.
     *
     * `limit` is capped at 200 by the route's own validator, so truncation is
     * possible and is DECLARED rather than hidden, exactly as the family list
     * does it.
     */
    sendCandidateTrfers: async (): Promise<SendCandidateTrfersResult> => {
      interface UserRow {
        _id: string;
        email?: string | null;
        profile?: { first_name?: string | null; last_name?: string | null };
      }
      interface UsersPayload { data?: UserRow[]; meta?: { total?: number } }
      /** `query('limit').isInt({ min: 1, max: 200 })` — shared/userManagement.js. */
      const LIMIT = 200;

      const response = await apiClient.get<UsersPayload>('/trfer/users', {
        params: { role: 'trfer', status: 'active', limit: LIMIT },
      });
      const rows = response.data.data ?? [];
      const total = Number(response.data.meta?.total ?? rows.length);

      const data: SendTrferOption[] = rows.map((u) => {
        const name = [u.profile?.first_name, u.profile?.last_name].filter(Boolean).join(' ').trim();
        // Falling back to the email keeps an unnamed colleague SELECTABLE. A
        // blank label would render a checkbox nobody can identify, which is a
        // worse failure than showing an address.
        return { _id: String(u._id), display: name || u.email || String(u._id) };
      });

      return { data, meta: { total: Math.max(total, data.length), truncated: rows.length >= LIMIT && total > rows.length } };
    },

    /** "Will reach N families" — the ONE query allowed to produce that number. */
    sendAudienceCount: async (
      audience: SendInstance['audience'],
    ): Promise<ApiResponse<{ count: number }>> => {
      const response = await apiClient.post<ApiResponse<{ count: number }>>(
        '/polls/audience-preview',
        { audience },
      );
      return response.data;
    },

    /**
     * B-409 — withdraw a pending send. `POST /api/polls/:id/cancel`.
     *
     * CONTRACT, read off the route (polls.js:913) and the CAS behind it
     * (`Poll.cancelPending`, Poll.js:367) rather than assumed:
     *   - accepts ONLY `scheduled` and `blocked`. Any other status is a 409
     *     naming the status it found; `open`/`closed` are refused on purpose,
     *     because once families have been notified, closing is the honest exit
     *     and un-sending is not available.
     *   - the poll goes to `draft`, with `scheduled_at`, `blocked_reason`,
     *     `blocked_at` and `published_at` all cleared. The minted questions
     *     survive, so the existing draft affordances apply again.
     *   - there is NO reschedule-to-a-new-time endpoint. Cancel → edit the
     *     draft → publish is the whole path, and it is what the blocked-send
     *     notification now tells staff to do.
     *   - returns the instance in its NEW state — the caller should render what
     *     came back, not guess.
     * A second 409 shape exists: the opener flipping the instance between the
     * route's read and its write ("This send already moved on — reload…").
     */
    cancelSend: async (instanceId: string): Promise<ApiResponse<SendInstance>> => {
      const response = await apiClient.post<ApiResponse<SendInstance>>(
        `/polls/${instanceId}/cancel`,
      );
      return response.data;
    },

    /** Aggregated answers for one minted instance (creator-or-admin). */
    sendResults: async (instanceId: string): Promise<ApiResponse<SendResultsPayload>> => {
      const response = await apiClient.get<ApiResponse<SendResultsPayload>>(
        `/polls/${instanceId}/results`,
      );
      return response.data;
    },
  },

  /**
   * Editable family-facing copy (#1039 / AP-26).
   */
  copy: {
    /**
     * PUBLIC — the published OVERRIDES for one copy document, plus the version
     * number and the cache floor. Reachable pre-auth because the invite-required
     * stop renders before any account exists.
     *
     * `fallbacks` is D-CS-3.5: how many times this browser has had to fall back
     * since its last successful read, seed and last-known-good counted apart.
     * It rides this read rather than a beacon of its own — a new pre-auth write
     * endpoint would be trivially spammable into a fake alarm. Zeroes are
     * omitted from the query string so the healthy case sends nothing.
     */
    published: async (
      slug: string,
      fallbacks?: { seed: number; last_known_good: number },
    ): Promise<ApiResponse<CopyPublishedResponse>> => {
      const params = new URLSearchParams();
      if (fallbacks?.seed) params.set('fell_back_to_seed', String(fallbacks.seed));
      if (fallbacks?.last_known_good) params.set('fell_back_to_lkg', String(fallbacks.last_known_good));
      const qs = params.toString();
      const response = await apiClient.get<ApiResponse<CopyPublishedResponse>>(
        `/copy/published/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`,
      );
      return response.data;
    },

    /* ── staff (#1039 PR-D) ────────────────────────────────────────────────
     * Every call below is behind authenticate + requireTRFerOrAdmin. The
     * three that change what a family reads — publish, restore, min-version —
     * additionally carry `can_publish_copy`, resolved against the ACTIVE role
     * (D-CS-5), which is why an admin browsing as a parent gets a 403 here and
     * a "switch to your staff view" banner on the page. */

    /** The working state: registry + published + draft + compact history. */
    get: async (slug: string): Promise<ApiResponse<CopyEditorDetail>> => {
      const response = await apiClient.get<ApiResponse<CopyEditorDetail>>(
        `/copy/${encodeURIComponent(slug)}`,
      );
      return response.data;
    },

    /** One slot's past wordings, WITH the text — the only way to recognise one. */
    slotHistory: async (
      slug: string,
      key: string,
    ): Promise<ApiResponse<CopySlotHistoryRow[]>> => {
      const response = await apiClient.get<ApiResponse<CopySlotHistoryRow[]>>(
        `/copy/${encodeURIComponent(slug)}/slots/${encodeURIComponent(key)}/history`,
      );
      return response.data;
    },

    /**
     * Save the working copy. `expected_updated_at` is the CAS token: two staff
     * editing at once is last-writer-wins WITH a signal, never a silent
     * overwrite (spec §Risks, the forms precedent).
     */
    saveDraft: async (
      slug: string,
      payload: { values: Record<string, unknown>; expected_updated_at?: string },
    ): Promise<ApiResponse<CopyVersionBody>> => {
      const response = await apiClient.patch<ApiResponse<CopyVersionBody>>(
        `/copy/${encodeURIComponent(slug)}/draft`,
        payload,
      );
      return response.data;
    },

    publish: async (slug: string, changeNote: string): Promise<ApiResponse<CopyVersionBody>> => {
      const response = await apiClient.post<ApiResponse<CopyVersionBody>>(
        `/copy/${encodeURIComponent(slug)}/publish`,
        { change_note: changeNote },
      );
      return response.data;
    },

    /** One slot back to a prior wording, as a NEW published version. */
    restoreSlot: async (
      slug: string,
      key: string,
      toVersionN: number,
    ): Promise<ApiResponse<CopyVersionBody>> => {
      const response = await apiClient.post<ApiResponse<CopyVersionBody>>(
        `/copy/${encodeURIComponent(slug)}/restore`,
        { key, to_version_n: toVersionN },
      );
      return response.data;
    },

    /**
     * The whole document back to one earlier version, as a NEW published one
     * (D-CS-16's per-document action). A different verb from `restoreSlot` on
     * purpose: the two histories share no noun, and they share no button.
     */
    revertToVersion: async (
      slug: string,
      toVersionN: number,
    ): Promise<ApiResponse<CopyVersionBody>> => {
      const response = await apiClient.post<ApiResponse<CopyVersionBody>>(
        `/copy/${encodeURIComponent(slug)}/revert`,
        { to_version_n: toVersionN },
      );
      return response.data;
    },

    /**
     * Park the draft so a restore can proceed. RECOVERABLE by design — it goes
     * to `set_aside`, never to `superseded`, and the confirm says so, because
     * "discard your work and restore" is a fork nobody takes at 9pm.
     */
    setAsideDraft: async (slug: string): Promise<ApiResponse<CopyVersionBody | null>> => {
      const response = await apiClient.post<ApiResponse<CopyVersionBody | null>>(
        `/copy/${encodeURIComponent(slug)}/set-aside-draft`,
        {},
      );
      return response.data;
    },

    /** The retraction lever (D-CS-3.2). Answers with its own blast radius. */
    raiseMinVersion: async (
      slug: string,
      minCopyVersion: number,
      reason: string,
    ): Promise<ApiResponse<CopyMinVersionResult>> => {
      const response = await apiClient.post<ApiResponse<CopyMinVersionResult>>(
        `/copy/${encodeURIComponent(slug)}/min-version`,
        { min_copy_version: minCopyVersion, reason },
      );
      return response.data;
    },
  },

  /**
   * Renewal wizard (applicant-facing). Plan: F-027.
   */
  renewals: {
    getMine: async (): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.get<ApiResponse<FamilyApplication>>('/renewals/me');
      return response.data;
    },
    createDraft: async (): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>('/renewals');
      return response.data;
    },
    saveSection: async (
      id: string,
      section: string,
      values: Record<string, unknown>,
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.patch<ApiResponse<FamilyApplication>>(
        `/renewals/${id}/section`,
        { section, values },
      );
      return response.data;
    },
    /** #2756: the unload twin of `saveSection` (see patchSectionKeepalive). */
    saveSectionOnUnload: (
      id: string,
      section: string,
      values: Record<string, unknown>,
    ): Promise<ApiResponse<FamilyApplication>> => patchSectionKeepalive(`/renewals/${id}/section`, section, values),
    /** #2440: the renewal router's twin of `applications.saveResumeStep`. */
    saveResumeStep: async (id: string, step: string): Promise<ApiResponse<ResumeStepResult>> => {
      const response = await apiClient.patch<ApiResponse<ResumeStepResult>>(
        `/renewals/${id}/resume-step`,
        { resume_step: step },
      );
      return response.data;
    },
    submit: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(`/renewals/${id}/submit`);
      return response.data;
    },
    uploadDocument: async (
      id: string,
      formData: FormData,
      onProgress?: (percent: number) => void,
    ): Promise<ApiResponse<ApplicationDocumentMeta>> => {
      const response = await apiClient.post<ApiResponse<ApplicationDocumentMeta>>(
        `/renewals/${id}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // #2477: per-file progress for the documents roster.
          onUploadProgress: onProgress
            ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
            : undefined,
        },
      );
      return response.data;
    },
    listDocuments: async (id: string): Promise<ApiResponse<ApplicationDocumentMeta[]>> => {
      const response = await apiClient.get<ApiResponse<ApplicationDocumentMeta[]>>(
        `/renewals/${id}/documents`,
      );
      return response.data;
    },
    /** #2471: the bytes of one of the family's own documents, for preview. */
    documentFile: async (id: string, docId: string): Promise<Blob> => {
      const response = await apiClient.get<Blob>(`/renewals/${id}/documents/${docId}/file`, { responseType: 'blob' });
      return response.data;
    },
    /** #2471: remove one of the family's own uploads (draft only). */
    removeDocument: async (id: string, docId: string): Promise<ApiResponse<{ id: string; kind: string }>> => {
      const response = await apiClient.delete<ApiResponse<{ id: string; kind: string }>>(`/renewals/${id}/documents/${docId}`);
      return response.data;
    },
    /** #2471: Undo of `removeDocument`. */
    restoreDocument: async (id: string, docId: string): Promise<ApiResponse<{ id: string; kind: string }>> => {
      const response = await apiClient.post<ApiResponse<{ id: string; kind: string }>>(`/renewals/${id}/documents/${docId}/restore`);
      return response.data;
    },
  },

  /**
   * Referral codes (2026-10-28 redesign) — one list of F (Family) and P
   * (Partner) codes. Reads are staff-baseline; writes need
   * can_review_applications.
   */
  referralCodes: {
    list: async (): Promise<ApiResponse<ReferralCodeSummary[]>> => {
      const response = await apiClient.get<ApiResponse<ReferralCodeSummary[]>>('/admin/referral-codes');
      return response.data;
    },
    create: async (payload: ReferralCodeCreatePayload): Promise<ApiResponse<ReferralCodeSummary>> => {
      const response = await apiClient.post<ApiResponse<ReferralCodeSummary>>('/admin/referral-codes', payload);
      return response.data;
    },
    update: async (id: string, payload: ReferralCodeUpdatePayload): Promise<ApiResponse<ReferralCodeSummary>> => {
      const response = await apiClient.patch<ApiResponse<ReferralCodeSummary>>(`/admin/referral-codes/${id}`, payload);
      return response.data;
    },
    revoke: async (id: string): Promise<ApiResponse<ReferralCodeSummary>> => {
      const response = await apiClient.post<ApiResponse<ReferralCodeSummary>>(`/admin/referral-codes/${id}/revoke`);
      return response.data;
    },
    /** A code with families answers 409 ("{n} families — revoke instead"). */
    remove: async (id: string): Promise<ApiResponse<ReferralCodeDeleted>> => {
      const response = await apiClient.delete<ApiResponse<ReferralCodeDeleted>>(`/admin/referral-codes/${id}`);
      return response.data;
    },
    bulk: async (ids: string[], action: ReferralCodeBulkAction): Promise<ApiResponse<ReferralCodeBulkResult>> => {
      const response = await apiClient.post<ApiResponse<ReferralCodeBulkResult>>('/admin/referral-codes/bulk', { ids, action });
      return response.data;
    },
    /** The referral letter filled in for this code (2026-10-31). */
    letter: async (id: string, signerId?: string | null): Promise<ApiResponse<ReferralLetter>> => {
      const response = await apiClient.get<ApiResponse<ReferralLetter>>(`/admin/referral-codes/${id}/letter`, {
        params: signerId ? { signer_id: signerId } : undefined,
      });
      return response.data;
    },
    /** Saves the letter as a Word file, named by the server. */
    downloadLetter: async (id: string, signerId?: string | null): Promise<void> => {
      await downloadCommitteePdf(`/admin/referral-codes/${id}/letter/download${signerId ? `?signer_id=${signerId}` : ''}`, {}, 'Referral letter.docx', {
        serverName: true, type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
    },
    /** Pick a template for this code's letter, keep a hand edit of it, or drop the edit. */
    setLetter: async (
      id: string,
      payload:
        | { template_id: string }
        | { family_template_id: string }
        | { part?: 'main' | 'family'; subject?: string | null; body: string }
        | { part?: 'main' | 'family'; reset: true }
        | { footer: boolean },
    ): Promise<ApiResponse<{ id: string; letter: string }>> => {
      const response = await apiClient.patch<ApiResponse<{ id: string; letter: string }>>(`/admin/referral-codes/${id}/letter`, payload);
      return response.data;
    },
    /** Emails the letter to some of the code's contacts from the no-reply address. */
    sendLetter: async (id: string, payload: { to: string[]; cc_me: boolean; signer_id?: string | null }): Promise<ApiResponse<{ sent: ReferralLetter['sent'][number]; cc: string[] }>> => {
      const response = await apiClient.post<ApiResponse<{ sent: ReferralLetter['sent'][number]; cc: string[] }>>(
        `/admin/referral-codes/${id}/letter/send`, payload,
      );
      return response.data;
    },
  },

  /**
   * Referral letter templates (2026-11-01). Reads are staff-baseline; every
   * POST and PATCH needs can_review_applications.
   */
  letterTemplates: {
    list: async (): Promise<ApiResponse<LetterTemplateList>> => {
      const response = await apiClient.get<ApiResponse<LetterTemplateList>>('/admin/letter-templates');
      return response.data;
    },
    create: async (payload: LetterTemplatePayload & { source: LetterTemplate['source']; source_file?: string | null }): Promise<ApiResponse<LetterTemplate>> => {
      const response = await apiClient.post<ApiResponse<LetterTemplate>>('/admin/letter-templates', payload);
      return response.data;
    },
    update: async (id: string, payload: Partial<LetterTemplatePayload>): Promise<ApiResponse<LetterTemplate>> => {
      const response = await apiClient.patch<ApiResponse<LetterTemplate>>(`/admin/letter-templates/${id}`, payload);
      return response.data;
    },
    makeDefault: async (id: string): Promise<ApiResponse<LetterTemplate>> => {
      const response = await apiClient.post<ApiResponse<LetterTemplate>>(`/admin/letter-templates/${id}/default`);
      return response.data;
    },
    archive: async (id: string): Promise<ApiResponse<{ id: string; archived: true }>> => {
      const response = await apiClient.post<ApiResponse<{ id: string; archived: true }>>(`/admin/letter-templates/${id}/archive`);
      return response.data;
    },
    /** Reads an uploaded letter into a draft with its fields marked. Saves nothing. */
    importFile: async (file: File, audience: ReferralCodeKind): Promise<ApiResponse<LetterTemplateDraft>> => {
      const form = new FormData();
      form.append('file', file);
      form.append('audience', audience);
      const response = await apiClient.post<ApiResponse<LetterTemplateDraft>>('/admin/letter-templates/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    updateLetterhead: async (payload: Partial<Pick<Letterhead, 'signature_lines' | 'notice'>>): Promise<ApiResponse<Letterhead>> => {
      const response = await apiClient.patch<ApiResponse<Letterhead>>('/admin/letter-templates/letterhead', payload);
      return response.data;
    },
    uploadPicture: async (kind: LetterPictureKind, file: File): Promise<ApiResponse<Letterhead>> => {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post<ApiResponse<Letterhead>>(`/admin/letter-templates/letterhead/${kind}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    /** Back to the picture that ships with the platform. */
    restorePicture: async (kind: LetterPictureKind): Promise<ApiResponse<Letterhead>> => {
      const response = await apiClient.delete<ApiResponse<Letterhead>>(`/admin/letter-templates/letterhead/${kind}`);
      return response.data;
    },
    /** Every staff member who can sign a letter. */
    signers: async (): Promise<ApiResponse<LetterSigner[]>> => {
      const response = await apiClient.get<ApiResponse<LetterSigner[]>>('/admin/letter-templates/letterhead/signatures');
      return response.data;
    },
    updateSignature: async (userId: string, payload: Partial<LetterSignature>): Promise<ApiResponse<LetterSigner[]>> => {
      const response = await apiClient.patch<ApiResponse<LetterSigner[]>>(`/admin/letter-templates/letterhead/signatures/${userId}`, payload);
      return response.data;
    },
    preview: async (payload: { audience: ReferralCodeKind; subject: string; body: string; code_id?: string | null }): Promise<ApiResponse<LetterPreview>> => {
      const response = await apiClient.post<ApiResponse<LetterPreview>>('/admin/letter-templates/preview', payload);
      return response.data;
    },
  },

  /**
   * Admin — family applications review surface.
   */
  adminApplications: {
    /**
     * B-435: `limit`/`offset` are exposed because the server has always accepted
     * them and the client never sent them — so the queue silently took the
     * default of 100 and could not tell staff more existed.
     *
     * `meta.count` is the PAGE length. `meta.total` is how many match, and it is
     * `null` when the count failed — unknown, never 0 and never the page length.
     *
     * #1018 Task 14, Part 2: `referral_method` filters the queue (e.g. 'none'
     * for the unattributed queue). The backend ignores an unrecognized value
     * rather than 400ing or returning empty — mirrored here by typing the
     * param as ReferralMethod so a caller can't pass a typo'd string. The
     * total in `meta` honours the same filter.
     */
    list: async (
      params?: {
        status?: string;
        referral_method?: ReferralMethod;
        /** #1490 — new applications or renewals only. Absent = both kinds;
         *  the server 400s any other value. */
        kind?: 'initial' | 'renewal';
        /** #1018 AC3/AC4 — the origin facets. Both are ObjectId strings; the
         *  server 400s a malformed one rather than silently showing the whole
         *  queue under a pill that names one code or hospital. */
        application_code_id?: string;
        referral_hospital_id?: string;
        limit?: number;
        offset?: number;
        // AP-41: server-side ordering. An unknown value is ignored by the
        // route (it is not a filter, so it cannot fake an empty result) and
        // the applied ordering comes back in `meta.sort`.
        sort?: ApplicationSort;
        /**
         * #1182 (D-SE-28) — narrow the queue to applications with an
         * outstanding document. SERVER-SIDE, and that is the whole point:
         * filtering after the page window returns only matches inside the first
         * page, which silently hides the oldest and most-chased applications —
         * exactly the ones being looked for. `meta.total` is the FILTERED total
         * under this filter, and the server 400s an unrecognised bucket rather
         * than showing the whole queue under a pill that names one.
         */
        documents?: ChaseBucket;
        /**
         * The toolbar search (2026-09-13): family last name or patient first
         * name, matched on the server so the total honours it. Trimmed; the
         * route 400s a term over 80 characters.
         */
        q?: string;
        /** #2234 — only applications carried over from an earlier cycle. The
         *  server 400s any value but true. */
        carried_over?: true;
        /** A presentation's id, or `none` for open applications in no open presentation. */
        presentation?: string;
      },
    ): Promise<ApiResponse<FamilyApplication[]> & { meta: ApplicationListMeta }> => {
      const response = await apiClient.get<ApiResponse<FamilyApplication[]> & { meta: ApplicationListMeta }>(
        '/admin/applications',
        { params },
      );
      return response.data;
    },
    get: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.get<ApiResponse<FamilyApplication>>(`/admin/applications/${id}`);
      return response.data;
    },
    /** "Translate to English" (Victor 2026-11-02): the stored translations after the fill. */
    translate: async (id: string): Promise<ApiResponse<{ answer_translations: AnswerTranslations }>> => {
      const response = await apiClient.post<ApiResponse<{ answer_translations: AnswerTranslations }>>(`/admin/applications/${id}/translate`);
      return response.data;
    },
    /**
     * #1182 — which expected documents have arrived, for named applications.
     *
     * The same service the queue column and the chase filter run through, so a
     * surface asking about one application cannot get a different answer from
     * the one the worklist used. `data` is keyed by application id; ids that
     * named no live initial application come back in `meta.not_found` rather
     * than silently absent — absent would read as "we could not tell".
     */
    documentsSummary: async (
      ids: string[],
    ): Promise<ApiResponse<Record<string, ApplicationDocumentChase[]>>
      & { meta: { kinds: string[]; not_found: string[] } }> => {
      const response = await apiClient.get<ApiResponse<Record<string, ApplicationDocumentChase[]>>
        & { meta: { kinds: string[]; not_found: string[] } }>(
        '/admin/applications/documents-summary',
        { params: { ids: ids.join(',') } },
      );
      return response.data;
    },
    // #1018 Task 14, Part 2 — the unattributed queue's write half. Sets
    // referral_hospital_id / referral_hospital_other / referral_contact_name
    // on an existing application and stamps referral_method='staff_entered'.
    // The ONLY staff-side way to correct an application that started
    // referral_method: 'none' (POST /applications is idempotent per
    // applicant, so the family can never attach a source themselves after
    // the fact).
    recordReferral: async (
      id: string,
      payload: { hospital_id?: string | null; hospital_other?: string | null; contact_name?: string | null },
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/referral`,
        payload,
      );
      return response.data;
    },
    decide: async (
      id: string,
      payload: ApplicationDecisionPayload,
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.patch<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}`,
        payload,
      );
      return response.data;
    },
    /**
     * #2236 — record a withdrawal the family asked for by phone or email.
     * `reason` is required (1–1000 characters). Sends nothing to the family.
     * `grant` reports what happened to a renewal's grant request, if any.
     */
    withdraw: async (
      id: string,
      reason: string,
    ): Promise<ApiResponse<FamilyApplication> & { grant?: { grant_id: string; outcome: 'withdrawn' | 'left_live' } | null }> => {
      const response = await apiClient.post(`/admin/applications/${id}/withdraw`, { reason });
      return response.data;
    },
    /**
     * Mario, 2026-10-13 — reopen a withdrawn application, back to the status it
     * was withdrawn from. `reason` is required (1–1000 characters) and becomes
     * the History note. Sends nothing to the family. `grant` reports a
     * renewal's grant request as it stands: `live` (left as it was) or
     * `still_withdrawn` (withdrawn with the renewal; settle it on the Grants desk).
     */
    reopen: async (
      id: string,
      reason: string,
    ): Promise<ApiResponse<FamilyApplication> & {
      grant?: { grant_id: string; outcome: 'live' | 'still_withdrawn'; grant_status: string } | null;
    }> => {
      const response = await apiClient.post(`/admin/applications/${id}/reopen`, { reason });
      return response.data;
    },
    /**
     * #2927 — the dry run of deleting an application: what would go, as
     * counts, and whether the server allows it (an admitted one is refused
     * with the sentence in `refusal`). Deletes nothing.
     */
    deletePreview: async (id: string): Promise<ApiResponse<ApplicationDeletePreview>> => {
      const response = await apiClient.get<ApiResponse<ApplicationDeletePreview>>(`/admin/applications/${id}/delete-preview`);
      return response.data;
    },
    /**
     * #2927 — delete a non-admitted application outright, with its documents
     * and files, drop-off tokens and notifications, and the applicant account
     * only when nothing else is attached to it. `reason` is required (1–1000
     * characters) and goes on the audit row. The referral code's use stays
     * spent. Sends nothing to the family. 409 for an admitted application.
     */
    deleteApplication: async (id: string, reason: string): Promise<ApiResponse<ApplicationDeleteResult>> => {
      const response = await apiClient.delete<ApiResponse<ApplicationDeleteResult>>(`/admin/applications/${id}`, { data: { reason } });
      return response.data;
    },
    /** Opening a submitted application starts its review (2026-09-29). Idempotent server-side. */
    open: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(`/admin/applications/${id}/open`);
      return response.data;
    },
    /** 2026-09-29: save the committee summary and/or the waitlist star. */
    updateCommittee: async (
      id: string,
      payload: { summary?: string | null; waitlist_star?: boolean },
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.patch<ApiResponse<FamilyApplication>>(`/admin/applications/${id}/committee`, payload);
      return response.data;
    },
    /**
     * #2238 — save ONE committee prose slot as a new revision. `expected_rev` is
     * the slot's rev the editor opened against (`CommitteeHistory.slots[slot].rev`,
     * 0 for a slot never saved); a stale one is a 409 whose body carries
     * `current` (`CommitteeProseConflict`) — read it off the thrown error, since
     * axios rejects on 4xx. `text: null` clears the slot. `meta.rev` is the next
     * `expected_rev`.
     */
    saveCommitteeProse: async (
      id: string,
      slot: CommitteeProseSlot,
      payload: { text: string | null; expected_rev: number; note?: string | null },
    ): Promise<ApiResponse<FamilyApplication> & { meta?: CommitteeProseWriteMeta }> => {
      const response = await apiClient.patch<ApiResponse<FamilyApplication> & { meta?: CommitteeProseWriteMeta }>(
        `/admin/applications/${id}/committee/prose/${slot}`,
        payload,
      );
      return response.data;
    },
    /**
     * #2231 — record the staff recommendation (`null` clears it). The note is
     * required for waitlist and decline, at most 280 characters. A change
     * bumps the page rev, so a reviewed page needs reviewing again.
     */
    setCommitteeRecommendation: async (
      id: string,
      payload: { recommended_outcome: CommitteeRecommendedOutcome | null; recommendation_note?: string | null },
    ): Promise<ApiResponse<FamilyApplication> & { meta?: { rev: number; changed: boolean } }> =>
      (await apiClient.patch(`/admin/applications/${id}/committee/recommendation`, payload)).data,
    /** #2231 — "Mark reviewed" at the page rev the reviewer was looking at (a stale one is a 409). */
    markCommitteeReviewed: async (
      id: string,
      expectedRev: number,
    ): Promise<ApiResponse<FamilyApplication> & { meta?: { rev: number; changed: boolean } }> =>
      (await apiClient.post(`/admin/applications/${id}/committee/review`, { expected_rev: expectedRev })).data,
    /** #2231 — a second reviewer's initial; never the reviewer themself (400). */
    initialCommittee: async (
      id: string,
      expectedRev: number,
    ): Promise<ApiResponse<FamilyApplication> & { meta?: { rev: number; changed: boolean } }> =>
      (await apiClient.post(`/admin/applications/${id}/committee/initial`, { expected_rev: expectedRev })).data,
    /** #2238 — the committee page's versions (newest first) and each slot's text, limit and rev. */
    committeeHistory: async (id: string): Promise<ApiResponse<CommitteeHistory>> => {
      const response = await apiClient.get<ApiResponse<CommitteeHistory>>(`/admin/applications/${id}/committee/history`);
      return response.data;
    },
    /**
     * #2238 — restore version `rev` of one slot. The restore is a NEW revision;
     * history never shortens. A one-step undo is a restore of the slot's
     * previous rev. Same 409 shape as `saveCommitteeProse`.
     */
    restoreCommitteeRevision: async (
      id: string,
      payload: { slot: CommitteeProseSlot; rev: number; expected_rev: number; note?: string | null },
    ): Promise<ApiResponse<FamilyApplication> & { meta?: CommitteeProseWriteMeta }> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: CommitteeProseWriteMeta }>(
        `/admin/applications/${id}/committee/restore`,
        payload,
      );
      return response.data;
    },
    /**
     * #2229 — "Draft with assistant". `status` is whether the assistant may draft this
     * application (and why not) plus the latest job; the page polls it while a job runs.
     * `start` answers 202 with the running job, or 200 with `started: false`, the reason
     * and the story cut to length when the assistant may not draft. A job already running
     * is a 409 (`code: 'DRAFT_RUNNING'`).
     */
    committeeBioDraft: {
      status: async (id: string): Promise<ApiResponse<CommitteeBioDraftState>> => {
        const response = await apiClient.get<ApiResponse<CommitteeBioDraftState>>(`/admin/applications/${id}/committee/bio-draft`);
        return response.data;
      },
      start: async (id: string): Promise<ApiResponse<CommitteeBioDraftStart>> => {
        const response = await apiClient.post<ApiResponse<CommitteeBioDraftStart>>(`/admin/applications/${id}/committee/bio-draft`);
        return response.data;
      },
    },
    /**
     * #2239 — suggestions and comments on the committee page. `list` names
     * every person and says whether each anchor still fits; the writes answer
     * with the application. An accept whose words are gone or now ambiguous is
     * a 409 `STALE_ANCHOR` (the suggestion was set aside); its own author
     * accepting is a 403; a resolved thread is a 409 `RESOLVED`; a blocking
     * comment resolved with no note is a 400 — read them off the thrown error.
     */
    committeeAnnotations: {
      list: async (id: string): Promise<ApiResponse<CommitteeAnnotations>> => {
        const response = await apiClient.get<ApiResponse<CommitteeAnnotations>>(`/admin/applications/${id}/committee/annotations`);
        return response.data;
      },
      suggest: async (
        id: string,
        payload: {
          slot: CommitteeProseSlot; anchor: CommitteeAnchor; replacement: string;
          reason?: string | null; category?: CommitteeSuggestionCategory | null; evidence?: string[];
        },
      ): Promise<ApiResponse<FamilyApplication> & { meta?: { suggestion: CommitteeSuggestionRecord } }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: { suggestion: CommitteeSuggestionRecord } }>(
          `/admin/applications/${id}/committee/suggestions`,
          payload,
        );
        return response.data;
      },
      accept: async (
        id: string,
        suggestionId: string,
        payload: { note?: string | null; expected_rev?: number } = {},
      ): Promise<ApiResponse<FamilyApplication> & { meta?: CommitteeSuggestionDecisionMeta }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: CommitteeSuggestionDecisionMeta }>(
          `/admin/applications/${id}/committee/suggestions/${suggestionId}/accept`,
          payload,
        );
        return response.data;
      },
      reject: async (
        id: string,
        suggestionId: string,
        payload: { note?: string | null; expected_rev?: number } = {},
      ): Promise<ApiResponse<FamilyApplication> & { meta?: CommitteeSuggestionDecisionMeta }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: CommitteeSuggestionDecisionMeta }>(
          `/admin/applications/${id}/committee/suggestions/${suggestionId}/reject`,
          payload,
        );
        return response.data;
      },
      comment: async (
        id: string,
        payload: { slot: CommitteeProseSlot; text: string; anchor?: CommitteeAnchor | null; blocking?: boolean },
      ): Promise<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }>(
          `/admin/applications/${id}/committee/comments`,
          payload,
        );
        return response.data;
      },
      reply: async (
        id: string,
        commentId: string,
        text: string,
      ): Promise<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }>(
          `/admin/applications/${id}/committee/comments/${commentId}/replies`,
          { text },
        );
        return response.data;
      },
      resolve: async (
        id: string,
        commentId: string,
        note: string | null = null,
      ): Promise<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }> => {
        const response = await apiClient.post<ApiResponse<FamilyApplication> & { meta?: { comment: CommitteeCommentRecord } }>(
          `/admin/applications/${id}/committee/comments/${commentId}/resolve`,
          { note },
        );
        return response.data;
      },
    },
    /**
     * #2243 — the assistant's fact check and Check grammar on one prose slot.
     * `status` is whether the assistant may run here (and why not) plus the
     * latest run; the page polls it while a run is going. `start` answers 202
     * with the running job. A closed gate (the switch is off) is a 403 `ASSISTANT_CLOSED` (`CommitteeAssistClosed`), a slot with no
     * words a 400, a run already going a 409 `ASSIST_RUNNING` — read them off
     * the thrown error. Suggestions arrive in `committeeAnnotations.list` as
     * `source: 'llm'`; unsupported claims as blocking comments.
     */
    committeeAssist: {
      factCheck: {
        status: async (id: string): Promise<ApiResponse<CommitteeAssistState>> => {
          const response = await apiClient.get<ApiResponse<CommitteeAssistState>>(`/admin/applications/${id}/committee/fact-check`);
          return response.data;
        },
        start: async (id: string, slot: CommitteeProseSlot): Promise<ApiResponse<CommitteeAssistStart>> => {
          const response = await apiClient.post<ApiResponse<CommitteeAssistStart>>(`/admin/applications/${id}/committee/fact-check`, { slot });
          return response.data;
        },
      },
      /** "Check grammar": `status.allowed` is false until the server's grammar flag is on. */
      grammar: {
        status: async (id: string): Promise<ApiResponse<CommitteeAssistState>> => {
          const response = await apiClient.get<ApiResponse<CommitteeAssistState>>(`/admin/applications/${id}/committee/grammar`);
          return response.data;
        },
        start: async (id: string, slot: CommitteeProseSlot): Promise<ApiResponse<CommitteeAssistStart>> => {
          const response = await apiClient.post<ApiResponse<CommitteeAssistStart>>(`/admin/applications/${id}/committee/grammar`, { slot });
          return response.data;
        },
      },
    },
    /**
     * #2244 — how the assistant's suggested edits fare, by category and prompt
     * version, across every application. Counts only. `windowHours` (1–720,
     * default 24) is how long after an accept a person's change counts as
     * re-editing it. `csv` fetches the same report as a CSV file (a Blob to save).
     */
    committeeLlmSuggestionStats: {
      get: async (windowHours?: number): Promise<ApiResponse<CommitteeLlmSuggestionStats>> => {
        const response = await apiClient.get<ApiResponse<CommitteeLlmSuggestionStats>>(
          '/admin/applications/committee/llm-suggestion-stats',
          { params: windowHours === undefined ? {} : { window_hours: windowHours } },
        );
        return response.data;
      },
      csv: async (windowHours?: number): Promise<Blob> => {
        const response = await apiClient.get<Blob>('/admin/applications/committee/llm-suggestion-stats.csv', {
          params: windowHours === undefined ? {} : { window_hours: windowHours },
          responseType: 'blob',
        });
        return response.data;
      },
    },
    /**
     * #1002 — record a committee/board (or staff) decision with its reason.
     * Outcome-shaped front on the same transition `decide` makes; the server
     * refuses a decline without a reason.
     */
    recordDecision: async (
      id: string,
      payload: {
        outcome: 'recommended' | 'approved' | 'declined' | 'waitlisted';
        reason?: string;
        // #2232: optional; review_again_on only with outcome 'waitlisted'.
        conditions?: string;
        review_again_on?: string;
      },
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/decisions`,
        payload,
      );
      return response.data;
    },
    /**
     * #998 / #1003 — Send. The first family-visible event: sends the offer
     * (welcome) email and moves committee_approved → offer_sent ONLY on
     * delivery success. A 502 means nothing was sent and nothing moved.
     */
    sendOffer: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/send-offer`,
      );
      return response.data;
    },
    /**
     * Decline and waitlist notices — the pre-send check: the decision the
     * notice reports, the four-eyes warning and the composed email.
     */
    decisionNotice: async (
      id: string, opts?: { family_note?: string },
    ): Promise<ApiResponse<DecisionNoticeCheck>> => {
      // A renewal notice's note to the family travels in the POST body, never the
      // URL, so it stays out of server access logs (#2583, DECISIONS 2026-10-11).
      const response = await apiClient.post<ApiResponse<DecisionNoticeCheck>>(
        `/admin/applications/${id}/decision-notice`,
        opts?.family_note ? { family_note: opts.family_note } : {},
      );
      return response.data;
    },
    /**
     * the Incomplete preview (ruled 2026-10-11): the Incomplete email exactly as
     * the family would receive it, in English. Sends nothing and writes nothing.
     */
    moreInfoPreview: async (
      id: string,
      body: { message: string; flagged_documents?: { kind: string; reason: string }[] },
    ): Promise<ApiResponse<MoreInfoPreview>> => {
      const response = await apiClient.post<ApiResponse<MoreInfoPreview>>(`/admin/applications/${id}/more-info-preview`, body);
      return response.data;
    },
    /**
     * Send the decline or waitlist notice, in English like every email TRF
     * sends (DECISIONS 2026-10-11). `previewed.original_reason` is the reason
     * the preview showed; a 409 means it moved and nothing was sent.
     * 502/503: nothing sent.
     */
    sendDecisionNotice: async (
      id: string, previewed?: DecisionNoticeSend,
    ): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/send-decision-notice`,
        { ...(previewed ?? {}) },
      );
      return response.data;
    },
    /**
     * DECISIONS 2026-10-16: record that TRF emailed the family about its
     * decline or waitlist. Sends nothing; stamps notice_sent_at.
     */
    recordDecisionNotice: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/record-decision-notice`,
        {},
      );
      return response.data;
    },
    /**
     * #999 — prototype: records the family's signature by hand until DocuSign
     * (#1043) lands. offer_sent → offer_signed, then admission.
     */
    markSigned: async (id: string): Promise<ApiResponse<FamilyApplication>> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication>>(
        `/admin/applications/${id}/mark-signed`,
      );
      return response.data;
    },
    /**
     * #991 → #999 — re-drive admission for an offer_signed application whose
     * admitApplication() threw during `markSigned` (#458). Idempotent
     * server-side. (The `warning` field is kept for shape compatibility; the
     * welcome now goes out at `sendOffer`, so admission no longer emails.)
     */
    retryAdmission: async (
      id: string,
    ): Promise<ApiResponse<FamilyApplication> & { warning?: string }> => {
      const response = await apiClient.post<ApiResponse<FamilyApplication> & { warning?: string }>(
        `/admin/applications/${id}/retry-admission`,
      );
      return response.data;
    },
    /**
     * #1490 — re-drive a committee-approved renewal's grant request when its
     * creation failed after the decision. Idempotent on the server: it never
     * creates a second grant.
     */
    createRenewalGrant: async (
      id: string,
    ): Promise<ApiResponse<FamilyApplication> & { meta?: { outcome: 'created' | 'adopted' | 'already_materialized' } }> => {
      const response = await apiClient.post<
        ApiResponse<FamilyApplication> & { meta?: { outcome: 'created' | 'adopted' | 'already_materialized' } }
      >(`/admin/applications/${id}/renewal-grant`);
      return response.data;
    },
    /**
     * Edit answers in place (AP-21 / #1004). Distinct from `decide` above,
     * which moves the application through its state machine — this changes what
     * the family said, and carries the whole write-through to the Family record
     * every other surface reads.
     *
     * `before` per path is the value the EDITOR WAS OPENED AGAINST, not a fresh
     * read: the server compares it to what is on file and refuses the write if
     * they differ (D-SE-4). Re-reading here before sending would make the
     * conflict check satisfied by construction and silently reinstate
     * last-writer-wins.
     *
     * A 409 carries `stale[]` on the error body — the caller must read it off
     * the thrown error, since axios rejects on 4xx.
     */
    patchFields: async (
      id: string,
      payload: { changes: FieldEditRequest[]; note?: string | null },
    ): Promise<ApiResponse<FieldEditResult>> => {
      const response = await apiClient.patch<ApiResponse<FieldEditResult>>(
        `/admin/applications/${id}/fields`,
        payload,
      );
      return response.data;
    },
    /**
     * The change history (#1008 / AP-25) — who changed what, when.
     *
     * Already ordered newest-first by the server, and rendered in the order it
     * arrives. There is ONE comparator in this system and it is not on the
     * client: a second one is a second answer to "what order is this in", and
     * the CSV below comes off the same endpoint, so the file and the screen
     * agree by construction rather than by two sorts happening to match.
     */
    history: async (id: string): Promise<ApiResponse<ChangeHistoryRow[]>> => {
      const response = await apiClient.get<ApiResponse<ChangeHistoryRow[]>>(
        `/admin/applications/${id}/history`,
      );
      return response.data;
    },

    /** Authed CSV download — mirrors the blob pattern in FamilyIntakeDrawer and
     *  grants.exportAwardsCsv. The route is staff-gated, so a plain <a href>
     *  would not carry the token. */
    exportHistoryCsv: async (id: string): Promise<void> => {
      const res = await apiClient.get<Blob>(`/admin/applications/${id}/history.csv`, {
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `application-${id}-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    /**
     * 2026-10-10 application review redesign — a free-text note on the
     * application's change history. A trail row only (`review.history_note`).
     */
    addHistoryNote: async (id: string, note: string): Promise<ApiResponse<ChangeHistoryRow>> => {
      const response = await apiClient.post<ApiResponse<ChangeHistoryRow>>(
        `/admin/applications/${id}/history/notes`,
        { note },
      );
      return response.data;
    },

    /**
     * 2026-10-10 application review redesign — the intake-review assistant and
     * the staff review actions on its checks. Every write returns the whole
     * view and lands one `review.*` row in the change history. `checkId` is one
     * of the ten assistant ids, or `staff:<id>` for a staff-added check. `start`
     * is 202 with the job, 409 while one runs, 403 when the switch is off.
     */
    intakeReview: {
      get: async (id: string): Promise<ApiResponse<IntakeReview>> => {
        const response = await apiClient.get<ApiResponse<IntakeReview>>(`/admin/applications/${id}/intake-review`);
        return response.data;
      },
      start: async (id: string): Promise<ApiResponse<{ job: IntakeReviewJob }>> => {
        const response = await apiClient.post<ApiResponse<{ job: IntakeReviewJob }>>(`/admin/applications/${id}/intake-review`);
        return response.data;
      },
      note: async (id: string, checkId: string, note: string): Promise<ApiResponse<IntakeReview>> => {
        const response = await apiClient.post<ApiResponse<IntakeReview>>(
          `/admin/applications/${id}/intake-review/checks/${encodeURIComponent(checkId)}/notes`,
          { note },
        );
        return response.data;
      },
      resolve: async (id: string, checkId: string, note: string): Promise<ApiResponse<IntakeReview>> => {
        const response = await apiClient.post<ApiResponse<IntakeReview>>(
          `/admin/applications/${id}/intake-review/checks/${encodeURIComponent(checkId)}/resolve`,
          { note },
        );
        return response.data;
      },
      reopen: async (id: string, checkId: string): Promise<ApiResponse<IntakeReview>> => {
        const response = await apiClient.post<ApiResponse<IntakeReview>>(
          `/admin/applications/${id}/intake-review/checks/${encodeURIComponent(checkId)}/reopen`,
        );
        return response.data;
      },
      addStaffCheck: async (
        id: string,
        payload: { label: string; finding: string; result: 'pass' | 'flag'; evidence?: IntakeEvidence[] },
      ): Promise<ApiResponse<IntakeReview>> => {
        const response = await apiClient.post<ApiResponse<IntakeReview>>(
          `/admin/applications/${id}/intake-review/staff-checks`,
          payload,
        );
        return response.data;
      },
    },

    /**
     * #1001 / AP-18 — the committee packet: every application awaiting
     * committee review as one PDF or one CSV. Byte-identical for unchanged
     * data, so the filename carries no date either. Same authed-blob pattern
     * as exportHistoryCsv above.
     *
     * #1632 — `acknowledged` is REQUIRED and is the caller's, not a constant.
     * The server refuses the export without it (403/400 from
     * `assertCommitteePacketExportAllowed`) and records it in the audit row, so
     * a call site that hard-codes `true` without asking anyone is writing a
     * false record. Pass what the dialog actually collected.
     *
     * THE REFUSAL HAS TO SURVIVE THE BLOB. This request asks axios for a Blob,
     * and axios applies that to the error response too — a 403 whose body is
     * `{"message":"The committee packet export is closed…"}` arrives with
     * `error.response.data` still an undecoded Blob, so `extractApiErrorMessage`
     * finds nothing and the reviewer is shown a generic "try again" for a gate
     * that a retry cannot open. `decodeBlobErrorBody` parses it back before the
     * error leaves this function; the caller still catches a real `AxiosError`.
     * Certified against the real path in `api.committeePacket.test.ts`.
     */
    downloadCommitteePacket: async (
      format: 'pdf' | 'csv' | 'zip',
      { acknowledged, ids }: { acknowledged: boolean; ids?: readonly string[] },
    ): Promise<void> => {
      // 2026-10-26: `ids` = the rows ticked in the Applications list; without it, the package.
      const res = await apiClient
        .get<Blob>(`/admin/applications/packet/${format}`, {
          responseType: 'blob',
          params: {
            ...(acknowledged ? { acknowledge: 'true' } : {}),
            ...(ids ? { ids: ids.join(',') } : {}),
          },
        })
        .catch(async (error: unknown) => { throw await decodeBlobErrorBody(error); });
      const type = format === 'pdf' ? 'application/pdf' : format === 'zip' ? 'application/zip' : 'text/csv';
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // QA B7: the server's dated name when it sends one; else the zip and the CSV are dated with the reviewer's day.
      a.download = dispositionFilename(res.headers?.['content-disposition'])
        ?? (format === 'pdf' ? `${ids ? 'selected-answers' : 'all-answers'}.pdf` : `application-answers-${localIsoDate()}.${format}`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    /**
     * Task 14 (2026-09-30) — one family's committee one-pager. Same blob +
     * refusal-decoding path as the packet above (`downloadCommitteePdf`).
     * Final review M3: dated with the reviewer's local date (`localIsoDate`).
     */
    downloadOnePager: async (
      id: string,
      { acknowledged, filenameHint }: { acknowledged: boolean; filenameHint?: string },
    ): Promise<{ overflow: string | null }> =>
      downloadCommitteePdf(
        `/admin/applications/${id}/one-pager.pdf`,
        { acknowledge: acknowledged ? 'true' : undefined, as_of: localIsoDate() },
        `committee-one-pager-${filenameHint ?? id}.pdf`,
      ),

    /**
     * One family's committee page as an editable PowerPoint slide in TRF's
     * template — the one-pager PDF's gate and download path, saved under the
     * server's name. No readiness refusal: a page still being written downloads.
     */
    downloadOnePagerPptx: async (
      applicationId: string,
      { acknowledged }: { acknowledged: boolean },
    ): Promise<void> => {
      await downloadCommitteePdf(
        `/admin/applications/${applicationId}/one-pager.pptx`,
        { acknowledge: acknowledged ? 'true' : undefined, as_of: localIsoDate() },
        `committee-one-pager-${applicationId}.pptx`,
        { serverName: true, type: PPTX_TYPE },
      );
    },

    /**
     * 2026-10-30: the photo the family's committee page prints, as the server
     * prepares it for the slide (upright, fitted, EXIF stripped), for the
     * Committee page's live preview. Null when the family has no photo on file
     * (the server answers 204). The route is staff-gated, so it is fetched as a
     * blob and shown through an object URL, never a plain <img src>.
     */
    committeePhoto: async (applicationId: string): Promise<Blob | null> => {
      const response = await apiClient.get<Blob>(`/admin/applications/${applicationId}/committee-photo`, { responseType: 'blob' });
      if (response.status === 204 || !(response.data instanceof Blob) || response.data.size === 0) return null;
      return response.data;
    },

    /**
     * 2026-10-11 Option A — the whole application as one PDF: the answers in
     * form order, the file list, then every PDF and image the family sent,
     * appended. `applicationPdf` returns the blob (the Print button loads it in
     * a hidden frame); `downloadApplicationPdf` saves it.
     */
    applicationPdf: async (id: string): Promise<Blob> => {
      const res = await apiClient
        .get<Blob>(`/admin/applications/${id}/application.pdf`, { responseType: 'blob' })
        .catch(async (error: unknown) => { throw await decodeBlobErrorBody(error); });
      return res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
    },
    downloadApplicationPdf: async (id: string, filenameHint?: string): Promise<void> => {
      const res = await apiClient
        .get<Blob>(`/admin/applications/${id}/application.pdf`, { responseType: 'blob' })
        .catch(async (error: unknown) => { throw await decodeBlobErrorBody(error); });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `application-${filenameHint ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    },

    /**
     * AP-47 — one application cycle as CSV: one row per application, with the
     * first time it reached each state read off the application's own audit
     * trail, plus its needs-more-info round-trip count and its origin.
     *
     * A cycle-scoped mount (`/admin/application-cycles`), not an
     * application-scoped one — it is addressed by a cycle id. Same authed-blob
     * pattern as `exportHistoryCsv` above: the route is staff-gated, so a
     * plain `<a href>` would not carry the token. The server names the file;
     * the fallback below is only for a response with no
     * `Content-Disposition`.
     */
    exportCycleCsv: async (cycleId: string): Promise<void> => {
      const res = await apiClient.get<Blob>(`/admin/application-cycles/${cycleId}/export.csv`, {
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
      const match = /filename="([^"]+)"/.exec(String(res.headers?.['content-disposition'] ?? ''));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match
        ? match[1]
        : `application-cycle-${cycleId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // ── #1017: application cycles — the annual container codes mint into ──
    listCycles: async (): Promise<ApiResponse<ApplicationCycleSummary[]>> => {
      const response = await apiClient.get<ApiResponse<ApplicationCycleSummary[]>>(
        '/admin/applications/cycles',
      );
      return response.data;
    },
    createCycle: async (
      payload: ApplicationCycleCreatePayload,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.post<ApiResponse<ApplicationCycleSummary>>(
        '/admin/applications/cycles',
        payload,
      );
      return response.data;
    },
    /** End now: the cycle stops accepting applications this instant (its deadline becomes now). */
    endCycleNow: async (id: string): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.post<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/end-now`,
      );
      return response.data;
    },
    /** Delete a cycle no application belongs to; the server answers 409 otherwise. */
    deleteCycle: async (id: string): Promise<ApiResponse<{ id: string; deleted: true }>> => {
      const response = await apiClient.delete<ApiResponse<{ id: string; deleted: true }>>(
        `/admin/applications/cycles/${id}`,
      );
      return response.data;
    },
    setCycleNotice: async (
      id: string,
      closed_notice: string,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.patch<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/notice`,
        { closed_notice },
      );
      return response.data;
    },
    /** Rename a cycle (its label; the key stays). */
    setCycleLabel: async (
      id: string,
      label: string,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.patch<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/label`,
        { label },
      );
      return response.data;
    },
    /** #2198: a cycle's opening date and hard deadline, as Pacific calendar dates. */
    setCycleDates: async (
      id: string,
      payload: ApplicationCycleDatesPayload,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.patch<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/dates`,
        payload,
      );
      return response.data;
    },
    /** #3092: a cycle's award terms (letter date, months, acceptance form due, onboarding meeting). */
    setCycleAwardTerms: async (
      id: string,
      payload: CycleAwardTermsPayload,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.patch<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/award-terms`,
        payload,
      );
      return response.data;
    },
    /** The referral letter's per-cycle lines (families per partner hospital, when decisions go out). */
    setCycleReferralTerms: async (
      id: string,
      payload: CycleReferralTermsPayload,
    ): Promise<ApiResponse<ApplicationCycleSummary>> => {
      const response = await apiClient.patch<ApiResponse<ApplicationCycleSummary>>(
        `/admin/applications/cycles/${id}/referral-terms`,
        payload,
      );
      return response.data;
    },
    /** launch-w1014 L1-a: the code requirement and required documents, writable by a lead. */
    updateIntakeSettings: async (updates: {
      application_codes_mode?: 'required' | 'optional' | 'disabled';
      application_required_documents?: string[];
    }): Promise<ApiResponse<{
      application_codes_mode: 'required' | 'optional' | 'disabled' | null;
      application_required_documents: string[] | null;
    }>> => {
      const response = await apiClient.patch<ApiResponse<{
        application_codes_mode: 'required' | 'optional' | 'disabled' | null;
        application_required_documents: string[] | null;
      }>>('/admin/applications/intake-settings', updates);
      return response.data;
    },
    // ── #477 (D-W2-20): staff-side application document access ──
    listDocuments: async (id: string): Promise<ApiResponse<ApplicationDocumentMeta[]>> => {
      const response = await apiClient.get<ApiResponse<ApplicationDocumentMeta[]>>(
        `/admin/applications/${id}/documents`,
      );
      return response.data;
    },
    /**
     * #1005 / AP-22: attach a document the family sent offline.
     *
     * `formData` carries `kind` and `document`, the same two fields the family
     * upload sends — the server applies the same allowlist, mime filter, size
     * limit and slot caps, and refuses a PHI kind unless THE FAMILY has an
     * accepted `phi_authorization`. A 403 here is that refusal and should be
     * shown as-is: it names something only the family can fix.
     */
    uploadDocument: async (
      id: string,
      formData: FormData,
    ): Promise<ApiResponse<ApplicationDocumentMeta>> => {
      const response = await apiClient.post<ApiResponse<ApplicationDocumentMeta>>(
        `/admin/applications/${id}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    /** Authed blob fetch — the route is staff-gated, so a plain <a href> won't carry the token. */
    getDocumentFile: async (id: string, docId: string): Promise<Blob> => {
      const response = await apiClient.get<Blob>(
        `/admin/applications/${id}/documents/${docId}/file`,
        { responseType: 'blob' },
      );
      return response.data;
    },
    /**
     * #416 — mint one single-use link a medical provider can use to hand in a
     * diagnosis letter without a TRF account.
     *
     * `link` carries the raw token and this response is the ONLY place it ever
     * appears: the server stores a hash, and no list or detail route echoes it
     * back. Treat it the way the UI treats a password reset link — show it,
     * let staff copy it, and do not persist it anywhere.
     */
    createProviderUploadToken: async (
      id: string,
      payload: { note?: string; replace?: boolean } = {},
    ): Promise<ApiResponse<ProviderUploadTokenMint>> => {
      const response = await apiClient.post<ApiResponse<ProviderUploadTokenMint>>(
        `/admin/applications/${id}/provider-upload-tokens`,
        payload,
      );
      return response.data;
    },
    /** #2160 — the provider requests minted for one application, newest first. Never returns a token. */
    listProviderUploadTokens: async (id: string): Promise<ApiResponse<ProviderUploadRequest[]>> => {
      const response = await apiClient.get<ApiResponse<ProviderUploadRequest[]>>(
        `/admin/applications/${id}/provider-upload-tokens`,
      );
      return response.data;
    },
  },

  /**
   * Committee presentations: named files of families for a committee meeting. At most one is
   * open; a sent file is a saved PDF. `pdfUrl` is the path (the apiClient base adds `/api`);
   * `downloadPdf` fetches it as a blob through the same refusal-decoding path as the packet.
   */
  committeePresentations: {
    list: async (): Promise<ApiResponse<CommitteePresentationSummary>> =>
      (await apiClient.get(COMMITTEE_PRESENTATIONS_PATH)).data,
    create: async (name: string): Promise<ApiResponse<PresentationTag>> =>
      (await apiClient.post(COMMITTEE_PRESENTATIONS_PATH, { name })).data,
    rename: async (id: string, name: string): Promise<ApiResponse<CommitteePresentationFile>> =>
      (await apiClient.patch(`${COMMITTEE_PRESENTATIONS_PATH}/${id}`, { name })).data,
    /** Sends the reviewer's local date as `as_of`, so the previews date and age as the download does. */
    families: async (id: string): Promise<ApiResponse<CommitteePresentationFamilies>> =>
      (await apiClient.get(`${COMMITTEE_PRESENTATIONS_PATH}/${id}/families`, { params: { as_of: localIsoDate() } })).data,
    add: async (
      id: string,
      ids: string[],
    ): Promise<ApiResponse<{ added: string[]; unchanged: string[]; skipped: Array<{ id: string; reason: 'not_found' | 'not_eligible' }> }>> =>
      (await apiClient.post(`${COMMITTEE_PRESENTATIONS_PATH}/${id}/applications`, { ids })).data,
    remove: async (id: string, applicationId: string): Promise<void> => {
      await apiClient.delete(`${COMMITTEE_PRESENTATIONS_PATH}/${id}/applications/${applicationId}`);
    },
    /** Sends the reviewer's local date as `as_of`, the day the saved PDF's cover prints. */
    markSent: async (id: string): Promise<ApiResponse<CommitteePresentationFile>> =>
      (await apiClient.post(`${COMMITTEE_PRESENTATIONS_PATH}/${id}/sent`, undefined, { params: { as_of: localIsoDate() } })).data,
    reopen: async (id: string): Promise<ApiResponse<CommitteePresentationFile>> =>
      (await apiClient.post(`${COMMITTEE_PRESENTATIONS_PATH}/${id}/reopen`)).data,
    /** #3434: each US region slide's partner hospital and state counts, and its participating states. */
    regionStats: async (): Promise<ApiResponse<{ regions: CommitteeRegionStatsRegion[] }>> =>
      (await apiClient.get(`${COMMITTEE_PRESENTATIONS_PATH}/region-stats`)).data,
    /** #3434: saves the regions sent, each replaced whole. */
    saveRegionStats: async (
      updates: Partial<Record<CommitteeRegionStatsRegion['region'], CommitteeRegionStatsValues>>,
    ): Promise<ApiResponse<{ regions: CommitteeRegionStatsRegion[] }>> =>
      (await apiClient.patch(`${COMMITTEE_PRESENTATIONS_PATH}/region-stats`, updates)).data,
    pdfUrl: (id: string, { acknowledge }: { acknowledge: boolean }): string =>
      `${COMMITTEE_PRESENTATIONS_PATH}/${id}/pdf${acknowledge ? '?acknowledge=true' : ''}`,
    downloadPdf: async (
      id: string,
      { acknowledge, filename }: { acknowledge: boolean; filename: string },
    ): Promise<{ overflow: string | null }> =>
      // Final review M3: dated with the reviewer's local date, as the package download was.
      // QA B7: `filename` is the stem; the fallback name carries the same as_of.
      downloadCommitteePdf(
        `${COMMITTEE_PRESENTATIONS_PATH}/${id}/pdf`,
        { acknowledge: acknowledge ? 'true' : undefined, as_of: localIsoDate() },
        `${filename}-${localIsoDate()}.pdf`,
        { serverName: true },
      ),
    /**
     * The presentation as an editable PowerPoint in TRF's template, the PDF's
     * pages from the families' current records (a sent file's saved PDF stays
     * the record). Same gate and download path as `downloadPdf`; `asOf`
     * defaults to the reviewer's local date (a sent file keeps the date its
     * PDF printed).
     */
    downloadPresentationPptx: async (
      id: string,
      { acknowledged, asOf }: { acknowledged: boolean; asOf?: string },
    ): Promise<void> => {
      const day = asOf ?? localIsoDate();
      await downloadCommitteePdf(
        `${COMMITTEE_PRESENTATIONS_PATH}/${id}/pptx`,
        { acknowledge: acknowledged ? 'true' : undefined, as_of: day },
        `committee-presentation-${day}.pptx`,
        { serverName: true, type: PPTX_TYPE },
      );
    },
    /**
     * 2026-10-30: ONE section page of the presentation — the cover (`cover`)
     * or a region's divider (`west`, `central`, …) — as a PDF or an editable
     * PowerPoint, from the families' current records. The same gate and
     * download path as the whole file, saved under the server's name.
     */
    downloadSection: async (
      id: string,
      section: string,
      { format, acknowledged, asOf }: { format: 'pdf' | 'pptx'; acknowledged: boolean; asOf?: string },
    ): Promise<{ overflow: string | null }> => {
      const day = asOf ?? localIsoDate();
      return downloadCommitteePdf(
        `${COMMITTEE_PRESENTATIONS_PATH}/${id}/sections/${encodeURIComponent(section)}/${format}`,
        { acknowledge: acknowledged ? 'true' : undefined, as_of: day },
        `committee-${section}-${day}.${format}`,
        { serverName: true, ...(format === 'pptx' ? { type: PPTX_TYPE } : {}) },
      );
    },
  },

  /**
   * #416 — the PUBLIC provider drop-off. Both calls are unauthenticated: the
   * caller is a medical provider holding a link, and the token in the path is
   * the whole authorization. `status` answers `{ valid }` and nothing else —
   * unknown, expired and already-used are one answer by design.
   */
  providerUploads: {
    status: async (token: string): Promise<ApiResponse<{ valid: boolean }>> => {
      const response = await apiClient.get<ApiResponse<{ valid: boolean }>>(
        `/provider-uploads/${token}/status`,
      );
      return response.data;
    },
    upload: async (
      token: string,
      formData: FormData,
    ): Promise<ApiResponse<{ received: boolean; filename: string }>> => {
      const response = await apiClient.post<ApiResponse<{ received: boolean; filename: string }>>(
        `/provider-uploads/${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
  },

  /**
   * Staff reports
   */
  reports: {
    /** Applications/admissions by hospital and by code (#1019). */
    applicationsByOrigin: async (cycleId?: string): Promise<ApiResponse<ApplicationOriginReport>> => {
      const qs = cycleId ? `?cycle_id=${encodeURIComponent(cycleId)}` : '';
      const response = await apiClient.get<ApiResponse<ApplicationOriginReport>>(`/trfer/reports/applications/by-origin${qs}`);
      return response.data;
    },
  },

  /**
   * BILL Spend & Expense — TRFer review queue (F-011 slice A).
   * Mock-mode fixtures until live wiring clears the 2026-07-05 finance memo.
   */
  spend: {
    queue: async (status?: string): Promise<ApiResponse<SpendQueueData>> => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const response = await apiClient.get<ApiResponse<SpendQueueData>>(`/trfer/spend/queue${qs}`);
      return response.data;
    },

    sync: async (): Promise<ApiResponse<SpendSyncResult>> => {
      const response = await apiClient.post<ApiResponse<SpendSyncResult>>('/trfer/spend/sync');
      return response.data;
    },

    approve: async (
      id: string,
      body?: { grant_id?: string; notes?: string },
    ): Promise<ApiResponse<{ transaction: SpendTransaction; grant_usage: SpendGrantUsage }>> => {
      const response = await apiClient.post<ApiResponse<{ transaction: SpendTransaction; grant_usage: SpendGrantUsage }>>(
        `/trfer/spend/${id}/approve`,
        body ?? {},
      );
      return response.data;
    },

    deny: async (
      id: string,
      body?: { notes?: string },
    ): Promise<ApiResponse<{ transaction: SpendTransaction }>> => {
      const response = await apiClient.post<ApiResponse<{ transaction: SpendTransaction }>>(
        `/trfer/spend/${id}/deny`,
        body ?? {},
      );
      return response.data;
    },
  },

  /**
   * DocuSign eSignature (epic #1043). Only the disclosure lives here so far —
   * the envelope endpoints arrive with the Wave-2 send flow.
   */
  docusign: {
    /**
     * #1668 — which DocuSign environment the platform is connected to.
     * `demo: true` means envelopes are watermarked and non-legal, and every
     * signature surface must say so on screen (`DemoEnvelopeNotice`). Read
     * once per page; a failure must never un-say a disclosure already shown —
     * callers keep the last known value (see useDocusignDisclosure).
     */
    disclosure: async (): Promise<ApiResponse<DocusignDisclosure>> => {
      const response = await apiClient.get<ApiResponse<DocusignDisclosure>>('/docusign/disclosure');
      return response.data;
    },
  },

  /**
   * Health Check
   */
  health: async (): Promise<ApiResponse<{ status: string; timestamp: string }>> => {
    const response = await apiClient.get<ApiResponse<{ status: string; timestamp: string }>>('/health');
    return response.data;
  },
};

export default api;
